import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PAYMENT_MODE_OPTIONS = [
  "OD Account",
  "Current Account",
  "Cash",
  "Bank Transfer",
  "Cheque",
  "UPI",
  "Other"
];

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const toDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const getTodayInput = () => toDateInput(new Date());

const getInvoiceTotal = (entry = {}) => {
  if (entry.finalTotal !== undefined && entry.finalTotal !== null && Number(entry.finalTotal) > 0) {
    return Number(entry.finalTotal);
  }
  if (Array.isArray(entry._lineItems) && entry._lineItems.length > 0) {
    const sum = entry._lineItems.reduce((acc, item) => acc + Number(item.totalAmount || 0), 0);
    if (sum > 0) return sum;
  }
  return Number(entry.totalAmount || 0);
};

const getAmountPaid = (entry = {}) => Number(entry.advanceAmount || entry.amountPaid || 0);

const getBalancePending = (entry = {}) => {
  const total = getInvoiceTotal(entry);
  const paid = getAmountPaid(entry);
  const noteAdjustment = Number(entry.noteAdjustmentAmount || 0);
  const calculatedBalance = Math.max(Number((total - paid - noteAdjustment).toFixed(2)), 0);

  if (entry.balanceAmount !== undefined && entry.balanceAmount !== null && entry.balanceAmount !== "") {
    const rawBalance = Number(entry.balanceAmount);
    if (rawBalance === 0 && paid === 0 && total > 0) {
      return calculatedBalance;
    }
    if (paid > 0 && rawBalance >= total) {
      return calculatedBalance;
    }
    if (rawBalance === 0 && paid < total) {
      return calculatedBalance;
    }
    return Math.min(Math.max(rawBalance, 0), calculatedBalance);
  }
  return calculatedBalance;
};

const getDocumentGroupKey = (entry = {}) => {
  const docNo = String(entry.documentNumber || "").trim();
  if (docNo) {
    return `${entry.entryType || "entry"}::doc::${docNo.toLowerCase()}`;
  }
  const poNo = String(entry.poNumber || "").trim();
  const party = String(entry.counterpartyName || "").trim().toLowerCase();
  if (poNo) {
    return `${entry.entryType || "entry"}::po::${poNo.toLowerCase()}::${party}`;
  }
  return String(entry.documentGroupKey || entry._id || "");
};

const getDaysRemaining = (dateValue) => {
  if (!dateValue) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const payDate = new Date(dateValue);
  if (Number.isNaN(payDate.getTime())) return null;
  payDate.setHours(0, 0, 0, 0);
  return Math.round((payDate.getTime() - today.getTime()) / 86400000);
};

const getSalesStatus = (entry) => {
  const total = getInvoiceTotal(entry);
  const paid = getAmountPaid(entry);
  const balance = getBalancePending(entry);
  if (total > 0 && paid === 0 && balance > 0) return "pending";
  if (balance <= 0) return "paid";
  if (entry.payDate) {
    const days = getDaysRemaining(entry.payDate);
    if (days !== null && days < 0) return "overdue";
  }
  if (paid > 0) return "partial";
  return "pending";
};

const getPurchaseStatus = (entry) => {
  const total = getInvoiceTotal(entry);
  const paid = getAmountPaid(entry);
  const balance = getBalancePending(entry);
  if (total > 0 && paid === 0 && balance > 0) {
    const days = getDaysRemaining(entry.payDate);
    if (days !== null && days < 0) return "overdue";
    if (days === null || days === 0) return "pending";
    return "upcoming";
  }
  if (balance <= 0) return "cleared";
  const days = getDaysRemaining(entry.payDate);
  if (days !== null && days < 0) return "overdue";
  if (paid > 0) return "partial";
  if (days === null || days === 0) return "pending";
  return "upcoming";
};

const statusBadgeClass = {
  pending: "bg-amber-100 text-amber-800",
  partial: "bg-sky-100 text-sky-800",
  paid: "bg-emerald-100 text-emerald-800",
  cleared: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-800",
  upcoming: "bg-violet-100 text-violet-800"
};

const statusLabel = {
  pending: "Pending",
  partial: "Partial",
  paid: "Paid",
  cleared: "Cleared",
  overdue: "Overdue",
  upcoming: "Upcoming"
};

const advanceStatusBadgeClass = {
  active: "bg-emerald-100 text-emerald-800 font-semibold",
  invoiced: "bg-blue-100 text-blue-800 font-semibold",
  adjusted: "bg-purple-100 text-purple-800 font-semibold",
  refunded: "bg-stone-100 text-stone-700 font-semibold",
  cancelled: "bg-rose-100 text-rose-800 font-semibold"
};

const advanceStatusLabel = {
  active: "Active (Advance Held)",
  invoiced: "Invoiced",
  adjusted: "Adjusted",
  refunded: "Refunded",
  cancelled: "Cancelled"
};

const groupInvoiceEntries = (entries = []) => {
  const groups = new Map();

  entries.forEach((entry) => {
    const groupKey = getDocumentGroupKey(entry);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        ...entry,
        _groupKey: groupKey,
        _lineItems: [entry]
      });
      return;
    }

    const group = groups.get(groupKey);
    if (!group._lineItems.some((item) => String(item._id) === String(entry._id))) {
      group._lineItems.push(entry);
    }
  });

  return Array.from(groups.values()).sort(
    (left, right) => new Date(right.transactionDate || right.createdAt || 0) - new Date(left.transactionDate || left.createdAt || 0)
  );
};

const buildDefaultModalForm = (type, entry) => {
  const pending = getBalancePending(entry);
  return {
    paymentDate: getTodayInput(),
    paymentMode: "",
    referenceNumber: "",
    amount: pending > 0 ? String(pending) : "",
    tdsAmount: "",
    notes: "",
    payDate: type === "purchase" ? toDateInput(entry?.payDate) : ""
  };
};

const buildDefaultAdvanceForm = (type = "customer", initialData = null) => {
  if (initialData) {
    return {
      partyName: initialData.partyName || "",
      customerId: initialData.customerId || "",
      supplierId: initialData.supplierId || "",
      workDescription: initialData.workDescription || "",
      amount: initialData.amount !== undefined ? String(initialData.amount) : "",
      date: toDateInput(initialData.date) || getTodayInput(),
      quotationId: initialData.quotationId || "",
      quotationNo: initialData.quotationNo || "",
      quotationAmount: initialData.quotationAmount ? String(initialData.quotationAmount) : "",
      paymentMode: initialData.paymentMode || "Current Account",
      referenceNumber: initialData.referenceNumber || "",
      expectedBillDate: toDateInput(initialData.expectedBillDate) || "",
      notes: initialData.notes || "",
      status: initialData.status || "active",
      invoiceNumber: initialData.invoiceNumber || ""
    };
  }

  return {
    partyName: "",
    customerId: "",
    supplierId: "",
    workDescription: "",
    amount: "",
    date: getTodayInput(),
    quotationId: "",
    quotationNo: "",
    quotationAmount: "",
    paymentMode: "Current Account",
    referenceNumber: "",
    expectedBillDate: "",
    notes: "",
    status: "active",
    invoiceNumber: ""
  };
};

const getCreatedByName = (isAdminView) =>
  isAdminView ? (localStorage.getItem("adminName") || "Admin") : (localStorage.getItem("accountsName") || "Accounts");

// ══ EXPORT UTILITIES ══

const exportAccountsToExcel = (type, dataList) => {
  if (!dataList || dataList.length === 0) {
    alert("No records available to export.");
    return;
  }

  const isSales = type === "sales";
  const totalBill = dataList.reduce((sum, item) => sum + getInvoiceTotal(item), 0);
  const totalPaid = dataList.reduce((sum, item) => sum + getAmountPaid(item), 0);
  const totalPending = dataList.reduce((sum, item) => sum + getBalancePending(item), 0);

  let rows = [];
  if (isSales) {
    rows = dataList.map((entry, idx) => ({
      "S.No": idx + 1,
      "Invoice Date": formatDate(entry.transactionDate),
      "Invoice No": entry.documentNumber || "-",
      "Customer Name": entry.counterpartyName || "-",
      "Reference": entry.reference || "-",
      "Total Invoice Amount (INR)": Number(getInvoiceTotal(entry).toFixed(2)),
      "Amount Paid (INR)": Number(getAmountPaid(entry).toFixed(2)),
      "Balance Pending (INR)": Number(getBalancePending(entry).toFixed(2)),
      "Status": statusLabel[getSalesStatus(entry)] || "Pending"
    }));

    rows.push({
      "S.No": "",
      "Invoice Date": "",
      "Invoice No": "TOTAL",
      "Customer Name": `${dataList.length} Invoices`,
      "Reference": "",
      "Total Invoice Amount (INR)": Number(totalBill.toFixed(2)),
      "Amount Paid (INR)": Number(totalPaid.toFixed(2)),
      "Balance Pending (INR)": Number(totalPending.toFixed(2)),
      "Status": ""
    });
  } else {
    rows = dataList.map((entry, idx) => {
      const days = getDaysRemaining(entry.payDate);
      return {
        "S.No": idx + 1,
        "Invoice Date": formatDate(entry.transactionDate),
        "Invoice No": entry.documentNumber || "-",
        "Supplier Name": entry.counterpartyName || "-",
        "Reference": entry.reference || "-",
        "Total Amount to Pay (INR)": Number(getInvoiceTotal(entry).toFixed(2)),
        "Amount Paid (INR)": Number(getAmountPaid(entry).toFixed(2)),
        "Balance to Pay (INR)": Number(getBalancePending(entry).toFixed(2)),
        "Pay Date": formatDate(entry.payDate),
        "Days Remaining / Overdue": days === null ? "-" : days < 0 ? `${Math.abs(days)} Overdue` : `${days} Days`,
        "Status": statusLabel[getPurchaseStatus(entry)] || "Pending"
      };
    });

    rows.push({
      "S.No": "",
      "Invoice Date": "",
      "Invoice No": "TOTAL",
      "Supplier Name": `${dataList.length} Invoices`,
      "Reference": "",
      "Total Amount to Pay (INR)": Number(totalBill.toFixed(2)),
      "Amount Paid (INR)": Number(totalPaid.toFixed(2)),
      "Balance to Pay (INR)": Number(totalPending.toFixed(2)),
      "Pay Date": "",
      "Days Remaining / Overdue": "",
      "Status": ""
    });
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();

  worksheet["!cols"] = isSales
    ? [
        { wch: 6 },
        { wch: 14 },
        { wch: 18 },
        { wch: 32 },
        { wch: 16 },
        { wch: 24 },
        { wch: 20 },
        { wch: 22 },
        { wch: 14 }
      ]
    : [
        { wch: 6 },
        { wch: 14 },
        { wch: 18 },
        { wch: 32 },
        { wch: 16 },
        { wch: 24 },
        { wch: 20 },
        { wch: 22 },
        { wch: 14 },
        { wch: 24 },
        { wch: 14 }
      ];

  const sheetTitle = isSales ? "Sales Accounts" : "Purchase Accounts";
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
  const dateStr = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `Genmech_${isSales ? "Sales" : "Purchase"}_Accounts_Report_${dateStr}.xlsx`);
};

const formatPdfCurrency = (value) => {
  const amount = Number(value || 0);
  return `Rs. ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatPdfDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).replace(/ /g, "-");
};

const exportAccountsToPdf = (type, dataList) => {
  if (!dataList || dataList.length === 0) {
    alert("No records available to export.");
    return;
  }

  const isSales = type === "sales";
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const totalBill = dataList.reduce((sum, item) => sum + getInvoiceTotal(item), 0);
  const totalPaid = dataList.reduce((sum, item) => sum + getAmountPaid(item), 0);
  const totalPending = dataList.reduce((sum, item) => sum + getBalancePending(item), 0);

  // Header Title
  doc.setFontSize(15);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("GENMECH SERVICE", 14, 13);

  doc.setFontSize(10.5);
  doc.setTextColor(51, 65, 85);
  doc.setFont("helvetica", "bold");
  doc.text(`${isSales ? "Sales Invoices Accounts Register" : "Purchase Invoices Accounts Register"}`, 14, 19);

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated on: ${new Date().toLocaleString("en-IN")}  |  Total Invoices: ${dataList.length}`, 14, 24);

  // Summary Metrics Banner Box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(14, 27, 269, 9, 2, 2, "FD");
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Total Invoices: ${dataList.length}    |    ` +
    `Total Bill: Rs. ${totalBill.toLocaleString("en-IN", { minimumFractionDigits: 2 })}    |    ` +
    `Total Paid: Rs. ${totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}    |    ` +
    `Total ${isSales ? "Pending" : "Balance to Pay"}: Rs. ${totalPending.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    18,
    33
  );

  let head, body, foot, columnStyles;
  if (isSales) {
    head = [["#", "Date", "Invoice No", "Customer Name", "Reference", "Total Amount", "Amount Paid", "Pending Balance", "Status"]];
    body = dataList.map((entry, idx) => [
      idx + 1,
      formatPdfDate(entry.transactionDate),
      entry.documentNumber || "-",
      entry.counterpartyName || "-",
      entry.reference || "-",
      formatPdfCurrency(getInvoiceTotal(entry)),
      formatPdfCurrency(getAmountPaid(entry)),
      formatPdfCurrency(getBalancePending(entry)),
      statusLabel[getSalesStatus(entry)] || "Pending"
    ]);
    foot = [[
      "Total",
      "",
      "",
      `${dataList.length} Invoices`,
      "",
      formatPdfCurrency(totalBill),
      formatPdfCurrency(totalPaid),
      formatPdfCurrency(totalPending),
      ""
    ]];
    columnStyles = {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: 28 },
      3: { cellWidth: "auto" },
      4: { cellWidth: 28 },
      5: { cellWidth: 32, halign: "right" },
      6: { cellWidth: 28, halign: "right" },
      7: { cellWidth: 32, halign: "right", fontStyle: "bold" },
      8: { cellWidth: 22, halign: "center" }
    };
  } else {
    head = [["#", "Date", "Invoice No", "Supplier Name", "Reference", "Total to Pay", "Amount Paid", "Balance to Pay", "Pay Date", "Days Left", "Status"]];
    body = dataList.map((entry, idx) => {
      const days = getDaysRemaining(entry.payDate);
      return [
        idx + 1,
        formatPdfDate(entry.transactionDate),
        entry.documentNumber || "-",
        entry.counterpartyName || "-",
        entry.reference || "-",
        formatPdfCurrency(getInvoiceTotal(entry)),
        formatPdfCurrency(getAmountPaid(entry)),
        formatPdfCurrency(getBalancePending(entry)),
        formatPdfDate(entry.payDate),
        days === null ? "-" : days < 0 ? `${Math.abs(days)}d Overdue` : `${days}d Left`,
        statusLabel[getPurchaseStatus(entry)] || "Pending"
      ];
    });
    foot = [[
      "Total",
      "",
      "",
      `${dataList.length} Invoices`,
      "",
      formatPdfCurrency(totalBill),
      formatPdfCurrency(totalPaid),
      formatPdfCurrency(totalPending),
      "",
      "",
      ""
    ]];
    columnStyles = {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 22 },
      2: { cellWidth: 28 },
      3: { cellWidth: "auto" },
      4: { cellWidth: 24 },
      5: { cellWidth: 28, halign: "right" },
      6: { cellWidth: 26, halign: "right" },
      7: { cellWidth: 28, halign: "right", fontStyle: "bold" },
      8: { cellWidth: 22 },
      9: { cellWidth: 22, halign: "center" },
      10: { cellWidth: 20, halign: "center" }
    };
  }

  autoTable(doc, {
    startY: 39,
    head: head,
    body: body,
    foot: foot,
    theme: "striped",
    headStyles: {
      fillColor: isSales ? [5, 150, 105] : [225, 29, 72],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8
    },
    footStyles: {
      fillColor: [241, 245, 249],
      textColor: [15, 23, 42],
      fontStyle: "bold",
      fontSize: 8
    },
    styles: {
      fontSize: 7.5,
      cellPadding: 2,
      textColor: [30, 41, 59],
      overflow: "linebreak",
      font: "helvetica"
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(str, doc.internal.pageSize.width - 24, doc.internal.pageSize.height - 8);
    }
  });

  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`Genmech_${isSales ? "Sales" : "Purchase"}_Accounts_Report_${dateStr}.pdf`);
};

// Export Advances (Customer or Supplier) to Excel & PDF
const exportAdvancesToExcel = (type, dataList) => {
  if (!dataList || dataList.length === 0) {
    alert("No advance records available to export.");
    return;
  }

  const isCustomer = type === "customer";
  const totalAdvance = dataList.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  let rows = [];
  if (isCustomer) {
    rows = dataList.map((entry, idx) => ({
      "S.No": idx + 1,
      "Date": formatDate(entry.date),
      "Customer Name": entry.partyName || "-",
      "Work Description": entry.workDescription || "-",
      "Quotation No": entry.quotationNo || "-",
      "Quotation Total (INR)": entry.quotationAmount ? Number(entry.quotationAmount) : "-",
      "Advance Amount (INR)": Number(Number(entry.amount || 0).toFixed(2)),
      "Payment Mode": entry.paymentMode || "-",
      "Reference No": entry.referenceNumber || "-",
      "Status": advanceStatusLabel[entry.status] || entry.status || "Active",
      "Linked Invoice": entry.invoiceNumber || "-"
    }));

    rows.push({
      "S.No": "",
      "Date": "",
      "Customer Name": "TOTAL",
      "Work Description": `${dataList.length} Advances`,
      "Quotation No": "",
      "Quotation Total (INR)": "",
      "Advance Amount (INR)": Number(totalAdvance.toFixed(2)),
      "Payment Mode": "",
      "Reference No": "",
      "Status": "",
      "Linked Invoice": ""
    });
  } else {
    rows = dataList.map((entry, idx) => ({
      "S.No": idx + 1,
      "Date": formatDate(entry.date),
      "Supplier Name": entry.partyName || "-",
      "Purpose / Work Description": entry.workDescription || "-",
      "Advance Amount Paid (INR)": Number(Number(entry.amount || 0).toFixed(2)),
      "Payment Mode": entry.paymentMode || "-",
      "Reference No": entry.referenceNumber || "-",
      "Expected Bill Date": formatDate(entry.expectedBillDate),
      "Status": advanceStatusLabel[entry.status] || entry.status || "Active",
      "Linked Invoice": entry.invoiceNumber || "-"
    }));

    rows.push({
      "S.No": "",
      "Date": "",
      "Supplier Name": "TOTAL",
      "Purpose / Work Description": `${dataList.length} Advances`,
      "Advance Amount Paid (INR)": Number(totalAdvance.toFixed(2)),
      "Payment Mode": "",
      "Reference No": "",
      "Expected Bill Date": "",
      "Status": "",
      "Linked Invoice": ""
    });
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();

  worksheet["!cols"] = isCustomer
    ? [
        { wch: 6 },
        { wch: 14 },
        { wch: 30 },
        { wch: 32 },
        { wch: 18 },
        { wch: 20 },
        { wch: 22 },
        { wch: 16 },
        { wch: 18 },
        { wch: 20 },
        { wch: 16 }
      ]
    : [
        { wch: 6 },
        { wch: 14 },
        { wch: 30 },
        { wch: 32 },
        { wch: 24 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 16 }
      ];

  const sheetTitle = isCustomer ? "Customer Advances" : "Supplier Advances";
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
  const dateStr = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `Genmech_${isCustomer ? "Customer_Sales" : "Supplier_Purchase"}_Advances_${dateStr}.xlsx`);
};

const exportAdvancesToPdf = (type, dataList) => {
  if (!dataList || dataList.length === 0) {
    alert("No advance records available to export.");
    return;
  }

  const isCustomer = type === "customer";
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const totalAdvance = dataList.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  doc.setFontSize(15);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("GENMECH SERVICE", 14, 13);

  doc.setFontSize(10.5);
  doc.setTextColor(51, 65, 85);
  doc.setFont("helvetica", "bold");
  doc.text(`${isCustomer ? "Customer (Sales) Advance Register" : "Supplier (Purchase) Advance Register"}`, 14, 19);

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated on: ${new Date().toLocaleString("en-IN")}  |  Total Entries: ${dataList.length}`, 14, 24);

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(14, 27, 269, 9, 2, 2, "FD");
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Total Advances Recorded: ${dataList.length}    |    ` +
    `Total Advance Amount: Rs. ${totalAdvance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    18,
    33
  );

  let head, body, foot, columnStyles;
  if (isCustomer) {
    head = [["#", "Date", "Customer Name", "Work Description", "Quotation #", "Advance Amount", "Payment Mode", "Ref #", "Status"]];
    body = dataList.map((entry, idx) => [
      idx + 1,
      formatPdfDate(entry.date),
      entry.partyName || "-",
      entry.workDescription || "-",
      entry.quotationNo ? `${entry.quotationNo}${entry.quotationAmount ? ` (Rs. ${Number(entry.quotationAmount).toLocaleString('en-IN')})` : ''}` : "-",
      formatPdfCurrency(entry.amount),
      entry.paymentMode || "-",
      entry.referenceNumber || "-",
      advanceStatusLabel[entry.status] || entry.status || "Active"
    ]);
    foot = [[
      "Total",
      "",
      `${dataList.length} Entries`,
      "",
      "",
      formatPdfCurrency(totalAdvance),
      "",
      "",
      ""
    ]];
    columnStyles = {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 48 },
      4: { cellWidth: 32 },
      5: { cellWidth: 30, halign: "right", fontStyle: "bold" },
      6: { cellWidth: 26 },
      7: { cellWidth: 26 },
      8: { cellWidth: 22, halign: "center" }
    };
  } else {
    head = [["#", "Date", "Supplier Name", "Purpose / Work", "Advance Amount Paid", "Payment Mode", "Ref #", "Expected Bill Date", "Status"]];
    body = dataList.map((entry, idx) => [
      idx + 1,
      formatPdfDate(entry.date),
      entry.partyName || "-",
      entry.workDescription || "-",
      formatPdfCurrency(entry.amount),
      entry.paymentMode || "-",
      entry.referenceNumber || "-",
      formatPdfDate(entry.expectedBillDate),
      advanceStatusLabel[entry.status] || entry.status || "Active"
    ]);
    foot = [[
      "Total",
      "",
      `${dataList.length} Entries`,
      "",
      formatPdfCurrency(totalAdvance),
      "",
      "",
      "",
      ""
    ]];
    columnStyles = {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 54 },
      4: { cellWidth: 34, halign: "right", fontStyle: "bold" },
      5: { cellWidth: 26 },
      6: { cellWidth: 26 },
      7: { cellWidth: 24 },
      8: { cellWidth: 22, halign: "center" }
    };
  }

  autoTable(doc, {
    startY: 39,
    head: head,
    body: body,
    foot: foot,
    theme: "striped",
    headStyles: {
      fillColor: isCustomer ? [5, 150, 105] : [225, 29, 72],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8
    },
    footStyles: {
      fillColor: [241, 245, 249],
      textColor: [15, 23, 42],
      fontStyle: "bold",
      fontSize: 8
    },
    styles: {
      fontSize: 7.5,
      cellPadding: 2,
      textColor: [30, 41, 59],
      overflow: "linebreak",
      font: "helvetica"
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(str, doc.internal.pageSize.width - 24, doc.internal.pageSize.height - 8);
    }
  });

  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`Genmech_${isCustomer ? "Customer_Sales" : "Supplier_Purchase"}_Advances_${dateStr}.pdf`);
};

// ══ REUSABLE UI CARDS & TABLES ══

function StatCard({ label, value, hint, tone = "amber" }) {
  const styles = {
    amber: {
      card: "border-amber-200/80 bg-gradient-to-br from-amber-50/70 via-white to-amber-50/40 text-amber-950",
      accent: "bg-amber-500",
      hint: "text-amber-800/80"
    },
    emerald: {
      card: "border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 via-white to-emerald-50/40 text-emerald-950",
      accent: "bg-emerald-500",
      hint: "text-emerald-800/80"
    },
    rose: {
      card: "border-rose-200/80 bg-gradient-to-br from-rose-50/70 via-white to-rose-50/40 text-rose-950",
      accent: "bg-rose-500",
      hint: "text-rose-800/80"
    },
    sky: {
      card: "border-sky-200/80 bg-gradient-to-br from-sky-50/70 via-white to-sky-50/40 text-sky-950",
      accent: "bg-sky-500",
      hint: "text-sky-800/80"
    },
    purple: {
      card: "border-purple-200/80 bg-gradient-to-br from-purple-50/70 via-white to-purple-50/40 text-purple-950",
      accent: "bg-purple-500",
      hint: "text-purple-800/80"
    }
  }[tone] || {
    card: "border-slate-200 bg-white text-slate-900",
    accent: "bg-slate-400",
    hint: "text-slate-500"
  };

  return (
    <div className={`group relative overflow-hidden rounded-2xl border p-4 shadow-xs hover:shadow-md transition-all duration-200 ${styles.card}`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${styles.accent}`} />
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
        <span className={`inline-block h-2 w-2 rounded-full ${styles.accent}`} />
      </div>
      <p className="mt-2 text-2xl font-black tracking-tight text-slate-950">{value}</p>
      {hint ? (
        <p className={`mt-1.5 text-[11px] font-medium leading-relaxed ${styles.hint}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function InputBlock({ label, error, required = false, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-stone-700">
        {label} {required ? <span className="text-rose-600">*</span> : null}
      </span>
      {children}
      {error ? <span className="mt-1 block text-xs font-medium text-rose-700">{error}</span> : null}
    </label>
  );
}

function SortHeader({ label, sortKey, currentSortBy, currentSortOrder, onSort, align = "left", className = "" }) {
  const isActive = currentSortBy === sortKey;
  const isRight = align === "right";

  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-4 py-3.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 cursor-pointer select-none transition-colors hover:bg-slate-100/80 ${isRight ? "text-right" : "text-left"} ${className}`}
      title={`Click to sort by ${label}`}
    >
      <div className={`inline-flex items-center gap-1.5 ${isRight ? "justify-end flex-row-reverse" : ""}`}>
        <span>{label}</span>
        <span className={`inline-flex h-4 w-4 items-center justify-center rounded text-[10px] ${
          isActive ? "bg-slate-900 text-white font-bold shadow-xs" : "text-slate-400"
        }`}>
          {isActive ? (currentSortOrder === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </div>
    </th>
  );
}

function ActionDropdown({ label = "Actions", items = [], align = "right" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Open upwards only if there is very little space below (< 210px) and significantly more space above
    const openUp = spaceBelow < 210 && spaceAbove > spaceBelow;

    let left = null;
    let right = null;

    if (align === "left") {
      left = Math.max(8, rect.left);
    } else {
      const rightDist = window.innerWidth - rect.right;
      if (rect.right < 210) {
        left = Math.max(8, rect.left);
      } else {
        right = Math.max(8, rightDist);
      }
    }

    if (openUp) {
      setCoords({
        top: null,
        bottom: Math.max(8, window.innerHeight - rect.top + 6),
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceAbove - 20))
      });
    } else {
      setCoords({
        top: Math.max(8, rect.bottom + 6),
        bottom: null,
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceBelow - 20))
      });
    }
  }, [align]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updateCoords();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    updateCoords();

    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (menuRef.current && menuRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleScrollOrResize = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updateCoords]);

  const visibleItems = items.filter(item => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-xs transition-all hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/20 active:scale-95 cursor-pointer"
      >
        <span>{label}</span>
        <span className={`text-[9px] text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
      </button>

      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            ...(coords.top !== null ? { top: `${coords.top}px` } : {}),
            ...(coords.bottom !== null ? { bottom: `${coords.bottom}px` } : {}),
            ...(coords.left !== null ? { left: `${coords.left}px` } : {}),
            ...(coords.right !== null ? { right: `${coords.right}px` } : {}),
            maxHeight: `${coords.maxHeight}px`,
            zIndex: 99999
          }}
          className="min-w-[170px] w-max max-w-[260px] overflow-y-auto rounded-xl border border-slate-200/90 bg-white p-1 shadow-2xl animate-fadeIn divide-y divide-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-0.5">
            {visibleItems.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  item.onClick?.();
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 cursor-pointer ${
                  item.danger
                    ? "text-rose-600 hover:bg-rose-50 font-semibold"
                    : item.highlight
                    ? "text-emerald-700 hover:bg-emerald-50 font-semibold"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {item.icon && <span className="text-xs shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function SalesSummaryCards({ pending, collected, advanceSum }) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatCard label="Sales Pending" value={formatCurrency(pending)} hint="Outstanding receivables on invoices" tone="amber" />
      <StatCard label="Sales Collected" value={formatCurrency(collected)} hint="Payments received against invoices" tone="emerald" />
      <StatCard label="Pre-Invoice Advances" value={formatCurrency(advanceSum)} hint="Advances received before invoice" tone="purple" />
    </div>
  );
}

function PurchaseSummaryCards({ balance, overdueCount, advanceSum }) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatCard label="Purchase Balance" value={formatCurrency(balance)} hint="Outstanding payables on bills" tone="rose" />
      <StatCard label="Purchase Overdue" value={String(overdueCount)} hint="Invoices already past pay date" tone="sky" />
      <StatCard label="Supplier Advances" value={formatCurrency(advanceSum)} hint="Advances paid to suppliers" tone="purple" />
    </div>
  );
}

function CustomerAdvanceSummaryCards({ totalAdvance, activeAdvance, invoicedAdvance, count }) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <StatCard label="Total Advances Received" value={formatCurrency(totalAdvance)} hint={`${count} total advance entries`} tone="emerald" />
      <StatCard label="Active Advance Held" value={formatCurrency(activeAdvance)} hint="Pending invoice generation" tone="amber" />
      <StatCard label="Invoiced / Adjusted" value={formatCurrency(invoicedAdvance)} hint="Linked / adjusted to invoices" tone="sky" />
      <StatCard label="Total Customer Advances" value={String(count)} hint="Active and completed" tone="purple" />
    </div>
  );
}

function SupplierAdvanceSummaryCards({ totalAdvance, activeAdvance, invoicedAdvance, count }) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <StatCard label="Total Advance Paid" value={formatCurrency(totalAdvance)} hint={`${count} total advance payments`} tone="rose" />
      <StatCard label="Active Pending Bill" value={formatCurrency(activeAdvance)} hint="Advance paid, bill pending" tone="amber" />
      <StatCard label="Settled / Billed" value={formatCurrency(invoicedAdvance)} hint="Bills received & adjusted" tone="emerald" />
      <StatCard label="Supplier Advance Count" value={String(count)} hint="Total advance records" tone="sky" />
    </div>
  );
}

function SalesFilters({ filters, onChange, onReset }) {
  return (
    <div className="flex flex-wrap items-end gap-3 text-xs bg-slate-50 p-3 rounded-[4px] border border-slate-200">
      <div>
        <label className="mb-1 block font-semibold text-stone-500">From Date</label>
        <input
          type="date"
          value={filters.dateFrom || ""}
          onChange={(e) => onChange("dateFrom", e.target.value)}
          className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
        />
      </div>
      <div>
        <label className="mb-1 block font-semibold text-stone-500">To Date</label>
        <input
          type="date"
          value={filters.dateTo || ""}
          onChange={(e) => onChange("dateTo", e.target.value)}
          className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
        />
      </div>
      <button
        type="button"
        onClick={onReset}
        className="rounded-[4px] border border-stone-300 px-3 h-8 font-semibold text-stone-700 transition hover:bg-stone-50 bg-white"
      >
        Reset Date
      </button>
    </div>
  );
}

function PurchaseFilters({ filters, onChange, onReset }) {
  return (
    <div className="flex flex-wrap items-end gap-3 text-xs bg-slate-50 p-3 rounded-[4px] border border-slate-200">
      <div>
        <label className="mb-1 block font-semibold text-stone-500">From Date</label>
        <input
          type="date"
          value={filters.dateFrom || ""}
          onChange={(e) => onChange("dateFrom", e.target.value)}
          className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
        />
      </div>
      <div>
        <label className="mb-1 block font-semibold text-stone-500">To Date</label>
        <input
          type="date"
          value={filters.dateTo || ""}
          onChange={(e) => onChange("dateTo", e.target.value)}
          className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
        />
      </div>
      <button
        type="button"
        onClick={onReset}
        className="rounded-[4px] border border-stone-300 px-3 h-8 font-semibold text-stone-700 transition hover:bg-stone-50 bg-white"
      >
        Reset Date
      </button>
    </div>
  );
}

function InvoiceItemsSection({ items = [], emptyLabel = "No invoice items found.", isAdmin = false, onEditInvoice }) {
  const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const subTotal = items.reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.unitPrice || 0)), 0);
  const grandTotal = items.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
  const totalTax = Math.max(grandTotal - subTotal, 0);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-stone-950">Invoice Items</h3>
          <p className="text-xs text-stone-500">Products and services billed in this invoice</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && onEditInvoice && (
            <button
              type="button"
              onClick={onEditInvoice}
              className="rounded-xl bg-slate-900 text-white px-3 py-1.5 text-xs font-bold hover:bg-black transition flex items-center gap-1.5 shadow-xs cursor-pointer"
              title="Edit line items and invoice details"
            >
              <span>✏️</span>
              <span>Edit Invoice Details</span>
            </button>
          )}
          <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-600">
            {items.length} {items.length === 1 ? "Item" : "Items"}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-stone-200">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50 text-left">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 w-12">#</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Item Name & Description</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">HSN/SAC</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">Quantity</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">Unit Price</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">GST %</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? items.map((item, idx) => {
              const qty = Number(item.quantity || 0);
              const unitPrice = Number(item.unitPrice || 0);
              const rowTotal = Number(item.totalAmount || 0);
              const gstRate = Number(item.gst || (Number(item.sgst || 0) + Number(item.cgst || 0)) || Number(item.igst || 0));

              return (
                <tr key={item._id || idx} className="border-t border-stone-200 hover:bg-stone-50/50">
                  <td className="px-4 py-3 text-sm text-stone-500">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-semibold text-stone-900">{item.itemName || "-"}</div>
                    {item.description && item.description !== item.itemName ? (
                      <div className="text-xs text-stone-500 line-clamp-1">{item.description}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">{item.hsnCode || "-"}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-stone-900">
                    {qty} <span className="text-xs text-stone-500">{item.unit || "No"}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-stone-700">{formatCurrency(unitPrice)}</td>
                  <td className="px-4 py-3 text-sm text-right text-stone-700">
                    {gstRate > 0 ? (
                      <span>
                        {gstRate}%
                        <span className="block text-[10px] text-stone-400">
                          {item.gstType === "igst" ? "IGST" : `${item.sgst || gstRate / 2}% SGST + ${item.cgst || gstRate / 2}% CGST`}
                        </span>
                      </span>
                    ) : (
                      <span className="text-stone-400">0%</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-stone-950">
                    {formatCurrency(rowTotal)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-sm text-stone-500">{emptyLabel}</td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot className="bg-stone-50/90 border-t border-stone-200 font-medium text-stone-800 text-sm">
              <tr>
                <td colSpan="3" className="px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-stone-600">
                  Total ({items.length} items)
                </td>
                <td className="px-4 py-2.5 text-right font-semibold text-stone-900">{totalQuantity}</td>
                <td className="px-4 py-2.5 text-right text-stone-600">{formatCurrency(subTotal)}</td>
                <td className="px-4 py-2.5 text-right text-stone-600">{formatCurrency(totalTax)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-stone-950">{formatCurrency(grandTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function PaymentHistorySection({ payments = [], emptyLabel }) {
  const totalAmount = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalTds = payments.reduce((sum, p) => sum + Number(p.tdsAmount || 0), 0);
  const totalSettled = totalAmount + totalTds;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-bold text-stone-950">Payment History</h3>
        <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-600">
          {payments.length} {payments.length === 1 ? "Entry" : "Entries"}
        </span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-stone-200">
        <table className="w-full min-w-[760px]">
          <thead className="bg-stone-50 text-left">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Date</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">Amount</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">TDS Amount</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500 text-right">Total Settled</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Payment Mode</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Reference Number</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Notes</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Created By</th>
            </tr>
          </thead>
          <tbody>
            {payments.length ? payments.map((payment) => (
              <tr key={payment._id} className="border-t border-stone-200">
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(payment.date)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-stone-900 text-right">{formatCurrency(payment.amount)}</td>
                <td className="px-4 py-3 text-sm font-medium text-amber-700 text-right">{Number(payment.tdsAmount) > 0 ? formatCurrency(payment.tdsAmount) : "-"}</td>
                <td className="px-4 py-3 text-sm font-bold text-stone-950 text-right">{formatCurrency((Number(payment.amount) || 0) + (Number(payment.tdsAmount) || 0))}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{payment.paymentMode || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{payment.referenceNumber || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{payment.notes || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700">{payment.createdBy || "-"}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8" className="px-4 py-6 text-center text-sm text-stone-500">{emptyLabel}</td>
              </tr>
            )}
          </tbody>
          {payments.length > 0 && (
            <tfoot className="bg-stone-50/90 border-t border-stone-200 font-medium text-stone-800 text-sm">
              <tr>
                <td colSpan="3" className="px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-stone-600">Total</td>
                <td className="px-4 py-2.5 text-right font-bold text-stone-900">{formatCurrency(totalAmount)}</td>
                <td className="px-4 py-2.5 text-right font-bold text-amber-700">{formatCurrency(totalTds)}</td>
                <td className="px-4 py-2.5 text-right font-extrabold text-stone-950">{formatCurrency(totalSettled)}</td>
                <td colSpan="4"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function PaymentModal({
  modalState,
  onClose,
  onViewChange,
  onFieldChange,
  onSubmit,
  isAdmin = false,
  onEditInvoice
}) {
  const {
    open,
    type,
    view,
    entry,
    history,
    loading,
    saving,
    error,
    notice,
    fieldErrors,
    form
  } = modalState;

  if (!open || !entry) return null;

  const totalAmount = Number(history?.totalAmount ?? getInvoiceTotal(entry));
  const previouslyPaidAmount = Number(history?.previouslyPaidAmount ?? getAmountPaid(entry));
  const noteAdjustmentAmount = Number(history?.noteAdjustmentAmount ?? entry.noteAdjustmentAmount ?? 0);
  const historyBalance = history?.balancePending;
  const balancePending = Number(
    historyBalance !== undefined && historyBalance !== null
      ? historyBalance
      : getBalancePending({ ...entry, advanceAmount: previouslyPaidAmount, finalTotal: totalAmount, noteAdjustmentAmount })
  );
  const payDateValue = type === "purchase" ? (form.payDate || toDateInput(history?.payDate || entry.payDate)) : "";
  const lineItems = history?.lineItems || entry._lineItems || [entry];
  const paymentsList = history?.payments || [];
  const percentPaid = totalAmount > 0 ? Math.min(Math.round((previouslyPaidAmount / totalAmount) * 100), 100) : 0;

  const currentStatus = type === "sales"
    ? getSalesStatus({ ...entry, advanceAmount: previouslyPaidAmount, balanceAmount: balancePending })
    : getPurchaseStatus({ ...entry, payDate: payDateValue || entry.payDate, balanceAmount: balancePending });

  const inputAmountNum = Number(form.amount || 0);
  const inputTdsNum = Number(form.tdsAmount || 0);
  const currentSettlement = Number((inputAmountNum + inputTdsNum).toFixed(2));
  const projectedBalance = Math.max(Number((balancePending - currentSettlement).toFixed(2)), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-3 md:p-6 animate-fadeIn">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-200">
        
        {/* ══ 1. HEADER ══ */}
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-5 text-white shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-400/20 px-2.5 py-0.5 text-[11px] font-bold tracking-wider text-amber-300 border border-amber-400/30 uppercase">
                  {type === "sales" ? "Sales Invoice" : "Purchase Invoice"}
                </span>
                <span className="font-mono text-sm font-extrabold text-white tracking-wide">
                  {entry.documentNumber || "No Invoice #"}
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                  currentStatus === "paid" || currentStatus === "cleared"
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                    : currentStatus === "partial"
                    ? "bg-sky-500/20 text-sky-300 border border-sky-500/40"
                    : currentStatus === "overdue"
                    ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                    : "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                }`}>
                  {statusLabel[currentStatus] || "Pending"}
                </span>
              </div>
              <h2 className="text-xl font-bold tracking-tight text-slate-100 flex items-center gap-2">
                <span>{entry.counterpartyName || "Counterparty"}</span>
              </h2>
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-300 font-medium">
                <span>📅 Invoice Date: <strong className="text-white">{formatDate(entry.transactionDate)}</strong></span>
                {type === "purchase" && (
                  <span>⏰ Pay Date: <strong className="text-amber-300">{formatDate(payDateValue || entry.payDate)}</strong></span>
                )}
                <span>📦 {lineItems.length} {lineItems.length === 1 ? "Line Item" : "Line Items"}</span>
                <span>💳 {paymentsList.length} {paymentsList.length === 1 ? "Split Payment Recorded" : "Split Payments Recorded"}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {isAdmin && onEditInvoice && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onEditInvoice(entry);
                  }}
                  className="rounded-xl bg-amber-400/20 border border-amber-400/50 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-400/30 hover:text-white transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                  title="Edit invoice details and line items (Admin only)"
                >
                  <span>✏️</span>
                  <span>Edit Invoice</span>
                </button>
              )}
            <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-full bg-slate-800/80 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/20 cursor-pointer"
                title="Close modal"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* ══ 2. FINANCIAL HERO KPI BANNER ══ */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-3.5 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            
            {/* Total Amount */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-xs flex flex-col justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Invoice Amount</span>
              <div className="mt-1 flex items-baseline justify-between">
                <span className="text-xl font-black text-slate-900">{formatCurrency(totalAmount)}</span>
                <span className="text-[11px] font-semibold text-slate-500">{lineItems.length} items</span>
              </div>
            </div>

            {/* Collected / Paid */}
            <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/50 to-white p-3.5 shadow-xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  {type === "sales" ? "Total Collected" : "Total Paid"}
                </span>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full">
                  {percentPaid}% Settled
                </span>
              </div>
              <div className="mt-1">
                <span className="text-xl font-black text-emerald-900">{formatCurrency(previouslyPaidAmount)}</span>
                <div className="mt-1.5 h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${percentPaid}%` }} />
                </div>
              </div>
            </div>

            {/* Balance Pending */}
            <div className={`rounded-2xl border p-3.5 shadow-xs flex flex-col justify-between ${
              balancePending > 0
                ? "border-amber-200/90 bg-gradient-to-br from-amber-50/70 to-white"
                : "border-emerald-200 bg-emerald-50/30"
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900">
                  {type === "sales" ? "Balance Pending" : "Balance to Pay"}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  balancePending > 0 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                }`}>
                  {balancePending > 0 ? `${paymentsList.length} Split Entries` : "Settled"}
                </span>
              </div>
              <div className="mt-1">
                <span className={`text-xl font-black ${balancePending > 0 ? "text-amber-950" : "text-emerald-900"}`}>
                  {formatCurrency(balancePending)}
                </span>
                <span className="block text-[10px] text-slate-500 mt-0.5">
                  {balancePending > 0 ? "Outstanding balance on this bill" : "Fully cleared"}
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* ══ 3. NAVIGATION SEGMENT TABS ══ */}
        <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-6 pt-3 shrink-0">
          <button
            type="button"
            onClick={() => onViewChange("payment")}
            className={`flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-xs font-bold transition-all border-b-2 cursor-pointer ${
              view === "payment"
                ? "border-slate-900 bg-slate-100 text-slate-950 font-extrabold"
                : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <span>💳</span>
            <span>{type === "sales" ? "Receive / Split Payment" : "Make / Split Payment"}</span>
            {balancePending > 0 && (
              <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold">
                {formatCurrency(balancePending)}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => onViewChange("items")}
            className={`flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-xs font-bold transition-all border-b-2 cursor-pointer ${
              view === "items"
                ? "border-slate-900 bg-slate-100 text-slate-950 font-extrabold"
                : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <span>📦</span>
            <span>Invoice Items</span>
            <span className="ml-1 rounded-full bg-slate-200 text-slate-700 px-2 py-0.5 text-[10px] font-bold">
              {lineItems.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onViewChange("history")}
            className={`flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-xs font-bold transition-all border-b-2 cursor-pointer ${
              view === "history"
                ? "border-slate-900 bg-slate-100 text-slate-950 font-extrabold"
                : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <span>📜</span>
            <span>Payment History</span>
            <span className="ml-1 rounded-full bg-slate-200 text-slate-700 px-2 py-0.5 text-[10px] font-bold">
              {paymentsList.length}
            </span>
          </button>
        </div>

        {/* ══ 4. TAB BODY (SCROLLABLE) ══ */}
        <div className="overflow-y-auto p-6 space-y-5 flex-1">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-500 flex flex-col items-center justify-center gap-2">
              <span className="animate-spin text-2xl">⏳</span>
              <span>Loading invoice records & payment history...</span>
            </div>
          ) : (
            <>
              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-xs font-semibold text-rose-800 flex items-center gap-2.5">
                  <span className="text-base">⚠️</span>
                  <span>{error}</span>
                </div>
              ) : null}

              {notice ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-800 flex items-center gap-2.5">
                  <span className="text-base">✅</span>
                  <span>{notice}</span>
                </div>
              ) : null}

              {view === "payment" && (
                <div className="space-y-5">
                  
                  {/* PREVIOUS SPLIT INSTALLMENTS TABLE (IF ANY) */}
                  {paymentsList.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                          <span>📜</span> Previous Split Installments ({paymentsList.length})
                        </h4>
                        <span className="text-xs font-bold text-emerald-800">
                          Total Settled: {formatCurrency(previouslyPaidAmount)} · Balance Left: {formatCurrency(balancePending)}
                        </span>
                      </div>
                      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100 text-slate-600 font-bold border-b border-slate-200 text-left">
                            <tr>
                              <th className="px-3 py-2 w-12 text-center">Entry</th>
                              <th className="px-3 py-2">Payment Date</th>
                              <th className="px-3 py-2 text-right">Amount Received</th>
                              <th className="px-3 py-2 text-right">TDS</th>
                              <th className="px-3 py-2 text-right font-bold">Total Settled</th>
                              <th className="px-3 py-2">Payment Mode</th>
                              <th className="px-3 py-2">Ref / Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {paymentsList.map((p, idx) => (
                              <tr key={p._id || idx} className="hover:bg-slate-50 transition">
                                <td className="px-3 py-2 text-center font-bold text-slate-500">
                                  <span className="inline-block rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                                    #{idx + 1}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-medium text-slate-800">{formatDate(p.date || p.paymentDate)}</td>
                                <td className="px-3 py-2 font-bold text-slate-900 text-right">{formatCurrency(p.amount)}</td>
                                <td className="px-3 py-2 text-amber-700 text-right">{Number(p.tdsAmount) > 0 ? formatCurrency(p.tdsAmount) : "-"}</td>
                                <td className="px-3 py-2 font-black text-emerald-700 text-right">{formatCurrency(p.totalSettled || ((Number(p.amount) || 0) + (Number(p.tdsAmount) || 0)))}</td>
                                <td className="px-3 py-2 text-slate-700">{p.paymentMode || "-"}</td>
                                <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate">{p.referenceNumber || p.notes || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* PAYMENT INPUT FORM CARD */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">
                          {type === "sales" ? "Record Payment Installment" : "Record Payment Entry"}
                          {paymentsList.length > 0 ? ` (Installment #${paymentsList.length + 1})` : ""}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {balancePending <= 0
                            ? "This invoice is fully settled. Additional entries can still be logged if needed."
                            : `Outstanding balance: ${formatCurrency(balancePending)}. Enter split amount below.`}
                        </p>
                      </div>
                      {balancePending > 0 && (
                        <button
                          type="button"
                          onClick={() => onFieldChange("amount", String(balancePending))}
                          className="rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100 transition cursor-pointer"
                          title="Fill full remaining balance"
                        >
                          ⚡ Fill Remaining ({formatCurrency(balancePending)})
                        </button>
                      )}
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      
                      {/* Payment Date */}
                      <InputBlock label="Payment Date" required error={fieldErrors.paymentDate}>
                        <input
                          type="date"
                          value={form.paymentDate}
                          onChange={(e) => onFieldChange("paymentDate", e.target.value)}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                        />
                      </InputBlock>

                      {/* Payment Mode */}
                      <InputBlock label="Payment Mode" required error={fieldErrors.paymentMode}>
                        <select
                          value={form.paymentMode}
                          onChange={(e) => onFieldChange("paymentMode", e.target.value)}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                        >
                          <option value="">Select Mode</option>
                          {PAYMENT_MODE_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </InputBlock>

                      {/* Reference Number */}
                      <InputBlock label="Reference / Cheque / UTR #">
                        <input
                          type="text"
                          value={form.referenceNumber}
                          onChange={(e) => onFieldChange("referenceNumber", e.target.value)}
                          placeholder="e.g. UTR12345678, CHQ-99182"
                          className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                        />
                      </InputBlock>

                      {/* Amount Receiving / Paying */}
                      <InputBlock
                        label={type === "sales" ? "Amount Receiving (INR)" : "Amount Paying (INR)"}
                        required
                        error={fieldErrors.amount}
                      >
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.amount}
                          onChange={(e) => onFieldChange("amount", e.target.value)}
                          placeholder="0.00"
                          className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-bold text-slate-900 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                        />
                      </InputBlock>

                      {/* TDS Amount */}
                      <InputBlock label="TDS Deducted (INR)" error={fieldErrors.tdsAmount}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.tdsAmount || ""}
                          onChange={(e) => onFieldChange("tdsAmount", e.target.value)}
                          placeholder="0.00 (Optional TDS)"
                          className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                        />
                      </InputBlock>

                      {/* Pay Date (Purchase only) or Notes */}
                      {type === "purchase" ? (
                        <InputBlock label="Next Due / Pay Date" error={fieldErrors.payDate}>
                          <input
                            type="date"
                            value={form.payDate}
                            onChange={(e) => onFieldChange("payDate", e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                          />
                        </InputBlock>
                      ) : (
                        <InputBlock label="Notes / Remarks">
                          <input
                            type="text"
                            value={form.notes}
                            onChange={(e) => onFieldChange("notes", e.target.value)}
                            placeholder="Optional internal remarks"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-900/10 transition"
                          />
                        </InputBlock>
                      )}

                    </div>

                    {/* LIVE REAL-TIME SETTLEMENT PREVIEW BOX */}
                    {currentSettlement > 0 && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3.5 text-xs text-emerald-950 flex flex-wrap items-center justify-between gap-3 shadow-xs">
                        <div className="flex items-center gap-3">
                          <span className="text-base">🧮</span>
                          <div>
                            <div className="font-semibold text-slate-700">
                              Amount: <strong className="text-slate-950">{formatCurrency(inputAmountNum)}</strong>
                              {inputTdsNum > 0 && (
                                <> + TDS: <strong className="text-amber-800">{formatCurrency(inputTdsNum)}</strong></>
                              )}
                              {" "}➔ Total Settled: <strong className="text-emerald-800 font-extrabold">{formatCurrency(currentSettlement)}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="text-right font-bold text-slate-800">
                          Remaining Balance After Save:{" "}
                          <span className={`text-sm font-black ${projectedBalance === 0 ? "text-emerald-700 font-extrabold" : "text-amber-800"}`}>
                            {formatCurrency(projectedBalance)}
                          </span>
                        </div>
                      </div>
                    )}

                  </div>

                </div>
              )}

              {view === "items" && (
                <InvoiceItemsSection
                  items={lineItems}
                  isAdmin={isAdmin}
                  onEditInvoice={() => {
                    onClose();
                    onEditInvoice?.(entry);
                  }}
                />
              )}

              {view === "history" && (
                <PaymentHistorySection
                  payments={paymentsList}
                  emptyLabel={type === "sales" ? "No sales payments recorded yet." : "No purchase payments recorded yet."}
                />
              )}
            </>
          )}
        </div>

        {/* ══ 5. STICKY FOOTER ══ */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 font-medium">
            {view === "payment" && currentSettlement > 0 ? (
              <span>
                Saving <strong className="text-slate-900 font-bold">{formatCurrency(currentSettlement)}</strong> settlement on invoice <strong className="text-slate-900 font-bold">{entry.documentNumber || ""}</strong>
              </span>
            ) : (
              <span>Invoice: <strong className="text-slate-800 font-bold">{entry.documentNumber || "-"}</strong> · Balance: <strong className="text-amber-800 font-bold">{formatCurrency(balancePending)}</strong></span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition disabled:opacity-50 cursor-pointer"
            >
              Cancel
            </button>

            {view === "payment" ? (
              <button
                type="button"
                onClick={onSubmit}
                disabled={saving || (balancePending <= 0 && currentSettlement <= 0)}
                className="rounded-xl bg-slate-950 px-5 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-black transition active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-400 flex items-center gap-1.5 cursor-pointer"
              >
                {saving ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Saving Payment...</span>
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    <span>
                      {currentSettlement > 0
                        ? `Confirm & Save ${formatCurrency(currentSettlement)}`
                        : "Save Payment Entry"}
                    </span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onViewChange("payment")}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-black transition cursor-pointer"
              >
                Go to Payment Form ➔
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// ══ ADVANCE RECORD & EDIT MODAL (CUSTOMER & SUPPLIER) ══

function AdvanceModal({
  modalState,
  onClose,
  onFieldChange,
  onQuotationSelect,
  onSubmit,
  customersList = [],
  dealersList = [],
  quotationsList = []
}) {
  const { open, type, mode, form, saving, error, fieldErrors } = modalState;
  const isCustomer = type === "customer";

  // Filter quotations for customer if customer selected
  const availableQuotations = useMemo(() => {
    if (!open || !isCustomer) return [];
    if (!form?.partyName) return quotationsList || [];
    const cName = form.partyName.trim().toLowerCase();
    return (quotationsList || []).filter(q => String(q.customerName || "").toLowerCase().includes(cName));
  }, [open, isCustomer, form?.partyName, quotationsList]);

  if (!open) return null;

  const title = mode === "edit"
    ? `Edit ${isCustomer ? "Customer" : "Supplier"} Advance`
    : `Record ${isCustomer ? "Customer Sales Advance (Pre-Invoice)" : "Supplier Purchase Advance"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 md:items-center md:p-6 animate-fadeIn">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4 md:px-6">
          <div>
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
              isCustomer ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
            }`}>
              {isCustomer ? "Sales Inflow Advance" : "Purchase Outflow Advance"}
            </span>
            <h2 className="mt-1 text-xl font-bold text-stone-950">{title}</h2>
            <p className="mt-0.5 text-xs text-stone-500">
              {isCustomer
                ? "Record customer advance payments received before raising a formal sales invoice."
                : "Record advance payments made to suppliers prior to receiving bills."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-stone-200 px-3 py-1.5 text-sm font-semibold text-stone-600 transition hover:border-stone-300 hover:text-stone-900"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5 md:px-6 space-y-4">
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Party Name / Customer / Supplier */}
            <div className="md:col-span-2">
              <InputBlock label={isCustomer ? "Customer Name" : "Supplier / Vendor Name"} required error={fieldErrors.partyName}>
                <div className="relative">
                  <input
                    type="text"
                    list={isCustomer ? "customer-suggestions" : "dealer-suggestions"}
                    value={form.partyName}
                    onChange={(e) => onFieldChange("partyName", e.target.value)}
                    placeholder={isCustomer ? "Select or enter customer name" : "Select or enter supplier name"}
                    className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800 font-medium"
                  />
                  <datalist id={isCustomer ? "customer-suggestions" : "dealer-suggestions"}>
                    {(isCustomer ? customersList : dealersList).map((item) => (
                      <option key={item._id || item.name} value={item.name}>
                        {item.name} {item.phone ? `(${item.phone})` : ""}
                      </option>
                    ))}
                  </datalist>
                </div>
              </InputBlock>
            </div>

            {/* Work Description / Subject */}
            <div className="md:col-span-2">
              <InputBlock label={isCustomer ? "Work / Service Description" : "Purpose / Material / Work Description"} required error={fieldErrors.workDescription}>
                <textarea
                  rows="2"
                  value={form.workDescription}
                  onChange={(e) => onFieldChange("workDescription", e.target.value)}
                  placeholder={isCustomer ? "e.g. 11kV VCB Servicing & Relay Testing at Unit 2" : "e.g. Advance payment for spare parts order"}
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                />
              </InputBlock>
            </div>

            {/* Advance Amount */}
            <div>
              <InputBlock label="Advance Amount (₹)" required error={fieldErrors.amount}>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-sm font-bold text-stone-400">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => onFieldChange("amount", e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-xl border border-stone-300 bg-white pl-8 pr-3.5 py-2.5 text-sm font-bold text-stone-900 outline-none focus:border-emerald-500"
                  />
                </div>
              </InputBlock>
            </div>

            {/* Date */}
            <div>
              <InputBlock label="Advance Date" required error={fieldErrors.date}>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => onFieldChange("date", e.target.value)}
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                />
              </InputBlock>
            </div>

            {/* Quotation Selection (Customer Advance only) */}
            {isCustomer && (
              <div className="md:col-span-2 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-900 flex items-center gap-1.5">
                    <span>📄</span> Link to Quotation (Optional)
                  </span>
                  {form.quotationNo && (
                    <button
                      type="button"
                      onClick={() => {
                        onFieldChange("quotationId", "");
                        onFieldChange("quotationNo", "");
                        onFieldChange("quotationAmount", "");
                      }}
                      className="text-[11px] font-semibold text-rose-600 hover:underline"
                    >
                      Clear Quotation
                    </button>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <select
                      value={form.quotationId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const found = quotationsList.find(q => String(q._id) === selectedId);
                        if (found) {
                          onQuotationSelect(found);
                        } else {
                          onFieldChange("quotationId", "");
                          onFieldChange("quotationNo", "");
                          onFieldChange("quotationAmount", "");
                        }
                      }}
                      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs outline-none focus:border-emerald-500 text-stone-700"
                    >
                      <option value="">Select from existing Quotations</option>
                      {(availableQuotations.length ? availableQuotations : quotationsList).map(q => (
                        <option key={q._id} value={q._id}>
                          {q.quotationNo} - {q.customerName} ({formatCurrency(q.grandTotal)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="text"
                      value={form.quotationNo}
                      onChange={(e) => onFieldChange("quotationNo", e.target.value)}
                      placeholder="Or enter quotation # manually"
                      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs outline-none focus:border-emerald-500 text-stone-700"
                    />
                  </div>
                </div>
                {form.quotationAmount > 0 && (
                  <p className="text-[11px] text-emerald-800 font-medium">
                    Quotation Total: <strong>{formatCurrency(form.quotationAmount)}</strong> · Advance covers {Math.min(Math.round((Number(form.amount || 0) / Number(form.quotationAmount)) * 100), 100)}% of quotation.
                  </p>
                )}
              </div>
            )}

            {/* Payment Mode */}
            <div>
              <InputBlock label="Payment Mode">
                <select
                  value={form.paymentMode}
                  onChange={(e) => onFieldChange("paymentMode", e.target.value)}
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                >
                  {PAYMENT_MODE_OPTIONS.map((mode) => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </InputBlock>
            </div>

            {/* Reference Number */}
            <div>
              <InputBlock label="Reference / UTR / Cheque #">
                <input
                  type="text"
                  value={form.referenceNumber}
                  onChange={(e) => onFieldChange("referenceNumber", e.target.value)}
                  placeholder="Transaction reference"
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                />
              </InputBlock>
            </div>

            {/* Expected Bill Date (Supplier Advance only) */}
            {!isCustomer && (
              <div>
                <InputBlock label="Expected Bill / Invoice Date">
                  <input
                    type="date"
                    value={form.expectedBillDate}
                    onChange={(e) => onFieldChange("expectedBillDate", e.target.value)}
                    className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                  />
                </InputBlock>
              </div>
            )}

            {/* Status */}
            <div>
              <InputBlock label="Advance Status">
                <select
                  value={form.status}
                  onChange={(e) => onFieldChange("status", e.target.value)}
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800 font-semibold"
                >
                  <option value="active">Active (Advance Held / Paid)</option>
                  <option value="invoiced">Invoiced (Linked to Invoice)</option>
                  <option value="adjusted">Adjusted / Settled</option>
                  <option value="refunded">Refunded</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </InputBlock>
            </div>

            {/* Linked Invoice # if status is invoiced */}
            {form.status === "invoiced" && (
              <div className="md:col-span-2">
                <InputBlock label="Linked Invoice Number">
                  <input
                    type="text"
                    value={form.invoiceNumber}
                    onChange={(e) => onFieldChange("invoiceNumber", e.target.value)}
                    placeholder="e.g. INV-2026-0089"
                    className="w-full rounded-xl border border-blue-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-blue-500 text-stone-800 font-semibold"
                  />
                </InputBlock>
              </div>
            )}

            {/* Notes */}
            <div className="md:col-span-2">
              <InputBlock label="Notes / Remarks">
                <textarea
                  rows="2"
                  value={form.notes}
                  onChange={(e) => onFieldChange("notes", e.target.value)}
                  placeholder="Optional internal remarks..."
                  className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500 text-stone-800"
                />
              </InputBlock>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-stone-200 bg-stone-50 px-5 py-3.5 md:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            className="rounded-xl bg-stone-950 px-5 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-black disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving ? "Saving Advance..." : mode === "edit" ? "Update Advance" : "Save Advance Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══ SALES & PURCHASE INVOICE TABLES ══

function SalesTable({
  data,
  loading,
  onAction,
  isAdmin = false,
  onEditInvoice,
  selectedKeys = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  sortBy,
  sortOrder,
  onSort
}) {
  const isAllSelected = data.length > 0 && data.every(entry => selectedKeys.has(entry._groupKey));
  const isSomeSelected = data.some(entry => selectedKeys.has(entry._groupKey)) && !isAllSelected;

  return (
    <div className="overflow-x-auto min-h-[220px] pb-24">
      <table className="w-full min-w-[1080px]">
        <thead className="bg-emerald-50 text-left border-b border-emerald-100">
          <tr>
            <th className="px-3 py-3 w-10 text-center">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                title="Select all"
              />
            </th>
            <SortHeader label="Invoice Date" sortKey="date" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Invoice No" sortKey="invoiceNo" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Customer" sortKey="customer" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Total Amount" sortKey="amount" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" />
            <SortHeader label="Amount Paid" sortKey="paid" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" />
            <SortHeader label="Balance Pending" sortKey="pending" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" className="bg-emerald-100/50" />
            <SortHeader label="Status" sortKey="status" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading && data.length === 0 ? (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">Loading sales invoices...</td></tr>
          ) : data.length ? data.map((entry) => {
            const status = getSalesStatus(entry);
            const isSelected = selectedKeys.has(entry._groupKey);
            return (
              <tr
                key={entry._groupKey}
                className={`border-t border-stone-200 transition ${
                  isSelected ? "bg-emerald-50/70" : "hover:bg-stone-50/50"
                }`}
              >
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(entry._groupKey)}
                    className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.transactionDate)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-stone-900">{entry.documentNumber || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 font-medium">{entry.counterpartyName || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 text-right">{formatCurrency(getInvoiceTotal(entry))}</td>
                <td className="px-4 py-3 text-sm text-stone-700 text-right">{formatCurrency(getAmountPaid(entry))}</td>
                <td className="px-4 py-3 text-sm font-bold text-stone-950 text-right bg-emerald-50/30">
                  {formatCurrency(getBalancePending(entry))}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass[status]}`}>{statusLabel[status]}</span>
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <ActionDropdown
                    label="Actions"
                    items={[
                      {
                        label: "Receive Payment",
                        icon: "💳",
                        highlight: true,
                        onClick: () => onAction("payment", entry)
                      },
                      {
                        label: "Edit Invoice",
                        icon: "✏️",
                        hidden: !isAdmin,
                        onClick: () => onEditInvoice?.(entry)
                      },
                      {
                        label: "View Items",
                        icon: "📦",
                        onClick: () => onAction("items", entry)
                      },
                      {
                        label: "View History",
                        icon: "📜",
                        onClick: () => onAction("history", entry)
                      }
                    ]}
                  />
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">No sales invoices found for the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PurchaseTable({
  data,
  loading,
  onAction,
  isAdmin = false,
  onEditInvoice,
  selectedKeys = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  sortBy,
  sortOrder,
  onSort
}) {
  const isAllSelected = data.length > 0 && data.every(entry => selectedKeys.has(entry._groupKey));
  const isSomeSelected = data.some(entry => selectedKeys.has(entry._groupKey)) && !isAllSelected;

  return (
    <div className="overflow-x-auto min-h-[220px] pb-24">
      <table className="w-full min-w-[1220px]">
        <thead className="bg-rose-50 text-left border-b border-rose-100">
          <tr>
            <th className="px-3 py-3 w-10 text-center">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-stone-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                title="Select all"
              />
            </th>
            <SortHeader label="Invoice Date" sortKey="date" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Invoice No" sortKey="invoiceNo" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Supplier" sortKey="supplier" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Total Amount" sortKey="amount" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" />
            <SortHeader label="Amount Paid" sortKey="paid" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" />
            <SortHeader label="Balance to Pay" sortKey="pending" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" className="bg-rose-100/50" />
            <SortHeader label="Pay Date" sortKey="dueDate" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Days Remaining" sortKey="dueDate" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading && data.length === 0 ? (
            <tr><td colSpan="11" className="px-4 py-6 text-center text-sm text-stone-500">Loading purchase invoices...</td></tr>
          ) : data.length ? data.map((entry) => {
            const days = getDaysRemaining(entry.payDate);
            const status = getPurchaseStatus(entry);
            const isSelected = selectedKeys.has(entry._groupKey);
            return (
              <tr
                key={entry._groupKey}
                className={`border-t border-stone-200 transition ${
                  isSelected ? "bg-rose-50/70" : "hover:bg-stone-50/50"
                }`}
              >
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(entry._groupKey)}
                    className="h-4 w-4 rounded border-stone-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.transactionDate)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-stone-900">{entry.documentNumber || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 font-medium">{entry.counterpartyName || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 text-right">{formatCurrency(getInvoiceTotal(entry))}</td>
                <td className="px-4 py-3 text-sm text-stone-700 text-right">{formatCurrency(getAmountPaid(entry))}</td>
                <td className="px-4 py-3 text-sm font-bold text-stone-950 text-right bg-rose-50/30">
                  {formatCurrency(getBalancePending(entry))}
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.payDate)}</td>
                <td className={`px-4 py-3 text-sm font-semibold ${days === null ? "text-stone-400" : days < 0 ? "text-rose-700" : days === 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {days === null ? "Set pay date" : days < 0 ? `${Math.abs(days)} Overdue` : `${days} Days Left`}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass[status]}`}>{statusLabel[status]}</span>
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <ActionDropdown
                    label="Actions"
                    items={[
                      {
                        label: "Make Payment",
                        icon: "💳",
                        highlight: true,
                        onClick: () => onAction("payment", entry)
                      },
                      {
                        label: "Edit Invoice",
                        icon: "✏️",
                        hidden: !isAdmin,
                        onClick: () => onEditInvoice?.(entry)
                      },
                      {
                        label: "View Items",
                        icon: "📦",
                        onClick: () => onAction("items", entry)
                      },
                      {
                        label: "View History",
                        icon: "📜",
                        onClick: () => onAction("history", entry)
                      }
                    ]}
                  />
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan="11" className="px-4 py-6 text-center text-sm text-stone-500">No purchase invoices found for the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ══ CUSTOMER ADVANCES TABLE (SALES PRE-INVOICE) ══

function CustomerAdvancesTable({
  data,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
  isAdmin = false,
  selectedKeys = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  sortBy,
  sortOrder,
  onSort
}) {
  const isAllSelected = data.length > 0 && data.every(entry => selectedKeys.has(entry._id));
  const isSomeSelected = data.some(entry => selectedKeys.has(entry._id)) && !isAllSelected;

  return (
    <div className="overflow-x-auto min-h-[220px] pb-24">
      <table className="w-full min-w-[1080px]">
        <thead className="bg-emerald-50 text-left border-b border-emerald-100">
          <tr>
            <th className="px-3 py-3 w-10 text-center">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                title="Select all"
              />
            </th>
            <SortHeader label="Date" sortKey="date" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Customer" sortKey="partyName" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Work Description" sortKey="workDescription" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Quotation #" sortKey="quotationNo" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Advance Amount" sortKey="amount" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" className="bg-emerald-100/50" />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-left">Payment Mode & Ref</th>
            <SortHeader label="Status" sortKey="status" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading && data.length === 0 ? (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">Loading customer advances...</td></tr>
          ) : data.length ? data.map((entry) => {
            const isSelected = selectedKeys.has(entry._id);
            return (
              <tr
                key={entry._id}
                className={`border-t border-stone-200 transition ${
                  isSelected ? "bg-emerald-50/70" : "hover:bg-stone-50/50"
                }`}
              >
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(entry._id)}
                    className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.date)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-stone-900">{entry.partyName || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 max-w-xs">
                  <div className="line-clamp-2">{entry.workDescription || "-"}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {entry.quotationNo ? (
                    <div className="inline-flex flex-col">
                      <span className="font-semibold text-emerald-800 bg-emerald-100/80 px-2 py-0.5 rounded text-xs">
                        {entry.quotationNo}
                      </span>
                      {entry.quotationAmount > 0 && (
                        <span className="text-[10px] text-stone-500 mt-0.5">
                          Total: {formatCurrency(entry.quotationAmount)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-stone-400 text-xs">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-emerald-800 text-right bg-emerald-50/30">
                  {formatCurrency(entry.amount)}
                </td>
                <td className="px-4 py-3 text-xs text-stone-700">
                  <div className="font-medium text-stone-900">{entry.paymentMode || "-"}</div>
                  {entry.referenceNumber && (
                    <div className="text-[11px] text-stone-500 font-mono">{entry.referenceNumber}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] ${advanceStatusBadgeClass[entry.status] || "bg-stone-100 text-stone-700"}`}>
                    {advanceStatusLabel[entry.status] || entry.status}
                  </span>
                  {entry.invoiceNumber && (
                    <div className="text-[10px] text-blue-700 font-semibold mt-0.5">
                      Inv: {entry.invoiceNumber}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-right">
                  <ActionDropdown
                    label="Actions"
                    items={[
                      {
                        label: "Edit Advance",
                        icon: "✏️",
                        hidden: !isAdmin,
                        onClick: () => onEdit(entry)
                      },
                      {
                        label: "Mark as Invoiced",
                        icon: "📜",
                        highlight: true,
                        hidden: entry.status !== "active",
                        onClick: () => onStatusChange(entry, "invoiced")
                      },
                      {
                        label: "Delete Advance",
                        icon: "🗑️",
                        danger: true,
                        hidden: !isAdmin,
                        onClick: () => onDelete(entry)
                      }
                    ]}
                  />
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">No customer advances recorded yet. Click &quot;Record Customer Advance&quot; to add one.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ══ SUPPLIER ADVANCES TABLE (PURCHASE ADVANCES) ══

function SupplierAdvancesTable({
  data,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
  isAdmin = false,
  selectedKeys = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  sortBy,
  sortOrder,
  onSort
}) {
  const isAllSelected = data.length > 0 && data.every(entry => selectedKeys.has(entry._id));
  const isSomeSelected = data.some(entry => selectedKeys.has(entry._id)) && !isAllSelected;

  return (
    <div className="overflow-x-auto min-h-[220px] pb-24">
      <table className="w-full min-w-[1080px]">
        <thead className="bg-rose-50 text-left border-b border-rose-100">
          <tr>
            <th className="px-3 py-3 w-10 text-center">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-stone-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                title="Select all"
              />
            </th>
            <SortHeader label="Date" sortKey="date" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Supplier Name" sortKey="partyName" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Purpose / Description" sortKey="workDescription" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Advance Paid" sortKey="amount" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} align="right" className="bg-rose-100/50" />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-left">Payment Mode & Ref</th>
            <SortHeader label="Expected Bill Date" sortKey="expectedBillDate" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-700 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading && data.length === 0 ? (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">Loading supplier advances...</td></tr>
          ) : data.length ? data.map((entry) => {
            const isSelected = selectedKeys.has(entry._id);
            return (
              <tr
                key={entry._id}
                className={`border-t border-stone-200 transition ${
                  isSelected ? "bg-rose-50/70" : "hover:bg-stone-50/50"
                }`}
              >
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(entry._id)}
                    className="h-4 w-4 rounded border-stone-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-stone-700">{formatDate(entry.date)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-stone-900">{entry.partyName || "-"}</td>
                <td className="px-4 py-3 text-sm text-stone-700 max-w-xs">
                  <div className="line-clamp-2">{entry.workDescription || "-"}</div>
                </td>
                <td className="px-4 py-3 text-sm font-bold text-rose-800 text-right bg-rose-50/30">
                  {formatCurrency(entry.amount)}
                </td>
                <td className="px-4 py-3 text-xs text-stone-700">
                  <div className="font-medium text-stone-900">{entry.paymentMode || "-"}</div>
                  {entry.referenceNumber && (
                    <div className="text-[11px] text-stone-500 font-mono">{entry.referenceNumber}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-stone-700">{formatDate(entry.expectedBillDate)}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] ${advanceStatusBadgeClass[entry.status] || "bg-stone-100 text-stone-700"}`}>
                    {advanceStatusLabel[entry.status] || entry.status}
                  </span>
                  {entry.invoiceNumber && (
                    <div className="text-[10px] text-blue-700 font-semibold mt-0.5">
                      Bill: {entry.invoiceNumber}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-right">
                  <ActionDropdown
                    label="Actions"
                    items={[
                      {
                        label: "Edit Advance",
                        icon: "✏️",
                        hidden: !isAdmin,
                        onClick: () => onEdit(entry)
                      },
                      {
                        label: "Mark as Settled / Billed",
                        icon: "📜",
                        highlight: true,
                        hidden: entry.status !== "active",
                        onClick: () => onStatusChange(entry, "adjusted")
                      },
                      {
                        label: "Delete Advance",
                        icon: "🗑️",
                        danger: true,
                        hidden: !isAdmin,
                        onClick: () => onDelete(entry)
                      }
                    ]}
                  />
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan="9" className="px-4 py-6 text-center text-sm text-stone-500">No supplier advances recorded yet. Click &quot;Record Supplier Advance&quot; to add one.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ViewMoreButton({ onClick, hasMore }) {
  if (!hasMore) return null;
  return (
    <div className="flex justify-center pt-6">
      <button
        type="button"
        onClick={onClick}
        className="rounded-[1.4rem] bg-stone-950 px-6 py-3.5 text-sm font-semibold text-white shadow-md transition hover:bg-black"
      >
        View More
      </button>
    </div>
  );
}

// ══ MAIN COMPONENT ══

export default function AccountsMonitor() {
  const location = useLocation();
  const navigate = useNavigate();
  const storedRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const isAdmin = location.pathname.startsWith("/admin") || storedRole === "admin";
  const isAdminView = isAdmin;

  // Navigation handlers for Admin Invoice Edit
  const handleEditSalesInvoice = useCallback((entry) => {
    if (!isAdmin || !entry) return;
    const lineItems = entry._lineItems && entry._lineItems.length > 0 ? entry._lineItems : [entry];
    navigate("/admin/sales-invoices", {
      state: {
        accountDocumentEditEntry: {
          ...entry,
          entryType: "sale",
          lineItems
        },
        prefillKey: `sales-invoice-edit-${entry._id || entry._groupKey}-${Date.now()}`
      }
    });
  }, [isAdmin, navigate]);

  const handleEditPurchaseInvoice = useCallback((entry) => {
    if (!isAdmin || !entry) return;
    const lineItems = entry._lineItems && entry._lineItems.length > 0 ? entry._lineItems : [entry];
    navigate("/admin/purchase-invoices", {
      state: {
        accountDocumentEditEntry: {
          ...entry,
          entryType: "purchase",
          lineItems
        },
        prefillKey: `purchase-invoice-edit-${entry._id || entry._groupKey}-${Date.now()}`
      }
    });
  }, [isAdmin, navigate]);

  const handleEditInvoiceFromModal = useCallback((type, entry) => {
    if (type === "sales") {
      handleEditSalesInvoice(entry);
    } else {
      handleEditPurchaseInvoice(entry);
    }
  }, [handleEditSalesInvoice, handleEditPurchaseInvoice]);

  // Invoices state
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Advances state
  const [customerAdvances, setCustomerAdvances] = useState([]);
  const [supplierAdvances, setSupplierAdvances] = useState([]);
  const [advancesLoading, setAdvancesLoading] = useState(true);

  // Metadata dropdowns
  const [customersList, setCustomersList] = useState([]);
  const [dealersList, setDealersList] = useState([]);
  const [quotationsList, setQuotationsList] = useState([]);

  // Active Main Tab ("sales" | "purchase")
  const [activeTab, setActiveTab] = useState("sales");

  // Sub-view Tab inside Sales ("invoices" | "advances") and Purchase ("invoices" | "advances")
  const [salesSubView, setSalesSubView] = useState("invoices");
  const [purchaseSubView, setPurchaseSubView] = useState("invoices");

  // Filter & Pagination States for Invoices
  const [salesFilters, setSalesFilters] = useState({
    search: "",
    invoiceNo: "",
    customerName: "",
    referenceNo: "",
    month: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "pending",
    sortOrder: "desc"
  });

  const [purchaseFilters, setPurchaseFilters] = useState({
    search: "",
    invoiceNo: "",
    supplierName: "",
    month: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "pending",
    sortOrder: "desc"
  });

  // Filter States for Advances
  const [customerAdvanceFilters, setCustomerAdvanceFilters] = useState({
    search: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "date",
    sortOrder: "desc"
  });

  const [supplierAdvanceFilters, setSupplierAdvanceFilters] = useState({
    search: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    sortBy: "date",
    sortOrder: "desc"
  });

  const [salesLimit, setSalesLimit] = useState(50);
  const [purchaseLimit, setPurchaseLimit] = useState(50);

  // Selection states
  const [selectedSalesKeys, setSelectedSalesKeys] = useState(new Set());
  const [selectedPurchaseKeys, setSelectedPurchaseKeys] = useState(new Set());
  const [selectedCustomerAdvanceKeys, setSelectedCustomerAdvanceKeys] = useState(new Set());
  const [selectedSupplierAdvanceKeys, setSelectedSupplierAdvanceKeys] = useState(new Set());

  // Search input values that are local until clicking Search/Enter
  const [salesSearchVal, setSalesSearchVal] = useState("");
  const [purchaseSearchVal, setPurchaseSearchVal] = useState("");
  const [customerAdvSearchVal, setCustomerAdvSearchVal] = useState("");
  const [supplierAdvSearchVal, setSupplierAdvSearchVal] = useState("");

  const [showSalesFilters, setShowSalesFilters] = useState(false);
  const [showPurchaseFilters, setShowPurchaseFilters] = useState(false);
  const [showCustomerAdvFilters, setShowCustomerAdvFilters] = useState(false);
  const [showSupplierAdvFilters, setShowSupplierAdvFilters] = useState(false);

  // Payment Modal State
  const [modalState, setModalState] = useState({
    open: false,
    type: "sales",
    view: "payment",
    entry: null,
    history: null,
    loading: false,
    saving: false,
    error: "",
    notice: "",
    fieldErrors: {},
    form: buildDefaultModalForm("sales")
  });

  // Advance Modal State
  const [advanceModalState, setAdvanceModalState] = useState({
    open: false,
    type: "customer", // "customer" | "supplier"
    mode: "create", // "create" | "edit"
    advanceId: null,
    saving: false,
    error: "",
    fieldErrors: {},
    form: buildDefaultAdvanceForm("customer")
  });

  // Fetch Invoices
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);

      const salesParams = {};
      if (salesFilters.dateFrom) salesParams.transactionDateFrom = salesFilters.dateFrom;
      if (salesFilters.dateTo) salesParams.transactionDateTo = salesFilters.dateTo;

      const purchaseParams = {};
      if (purchaseFilters.dateFrom) purchaseParams.transactionDateFrom = purchaseFilters.dateFrom;
      if (purchaseFilters.dateTo) purchaseParams.transactionDateTo = purchaseFilters.dateTo;

      const [salesRes, purchaseRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/account-documents", { params: { ...salesParams, entryType: "sale" } }),
        axios.get(API_BASE_URL + "/api/account-documents", { params: { ...purchaseParams, entryType: "purchase" } })
      ]);

      setSalesInvoices(Array.isArray(salesRes.data) ? salesRes.data : []);
      setPurchaseInvoices(Array.isArray(purchaseRes.data) ? purchaseRes.data : []);
    } catch (error) {
      setSalesInvoices([]);
      setPurchaseInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [salesFilters.dateFrom, salesFilters.dateTo, purchaseFilters.dateFrom, purchaseFilters.dateTo]);

  // Fetch Advances
  const loadAdvances = useCallback(async () => {
    try {
      setAdvancesLoading(true);
      const [custRes, suppRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/advance-payments", { params: { type: "customer" } }),
        axios.get(API_BASE_URL + "/api/advance-payments", { params: { type: "supplier" } })
      ]);
      setCustomerAdvances(Array.isArray(custRes.data) ? custRes.data : []);
      setSupplierAdvances(Array.isArray(suppRes.data) ? suppRes.data : []);
    } catch (err) {
      setCustomerAdvances([]);
      setSupplierAdvances([]);
    } finally {
      setAdvancesLoading(false);
    }
  }, []);

  // Fetch Customers, Dealers, Quotations for Autocomplete / Linking
  const loadMetadata = useCallback(async () => {
    try {
      const [custRes, dealerRes, quotRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/customers").catch(() => ({ data: [] })),
        axios.get(API_BASE_URL + "/api/dealers").catch(() => ({ data: [] })),
        axios.get(API_BASE_URL + "/api/quotations").catch(() => ({ data: [] }))
      ]);
      setCustomersList(Array.isArray(custRes.data) ? custRes.data : []);
      setDealersList(Array.isArray(dealerRes.data) ? dealerRes.data : []);
      setQuotationsList(Array.isArray(quotRes.data) ? quotRes.data : []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadAdvances();
    loadMetadata();
  }, [loadAdvances, loadMetadata]);

  const groupedSalesInvoices = useMemo(() => groupInvoiceEntries(salesInvoices), [salesInvoices]);
  const groupedPurchaseInvoices = useMemo(() => groupInvoiceEntries(purchaseInvoices), [purchaseInvoices]);

  // Compare helper for Invoices
  const compareInvoices = (a, b, sortBy, sortOrder, type) => {
    let valA, valB;

    if (type === "sales") {
      if (sortBy === "date") {
        valA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        valB = new Date(b.transactionDate || b.createdAt || 0).getTime();
      } else if (sortBy === "amount") {
        valA = getInvoiceTotal(a);
        valB = getInvoiceTotal(b);
      } else if (sortBy === "paid") {
        valA = getAmountPaid(a);
        valB = getAmountPaid(b);
      } else if (sortBy === "customer") {
        valA = String(a.counterpartyName || "").toLowerCase();
        valB = String(b.counterpartyName || "").toLowerCase();
      } else if (sortBy === "invoiceNo") {
        valA = String(a.documentNumber || "").toLowerCase();
        valB = String(b.documentNumber || "").toLowerCase();
      } else if (sortBy === "pending" || sortBy === "balance") {
        valA = getBalancePending(a);
        valB = getBalancePending(b);
      } else if (sortBy === "status") {
        valA = getSalesStatus(a);
        valB = getSalesStatus(b);
      } else {
        valA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        valB = new Date(b.transactionDate || b.createdAt || 0).getTime();
      }
    } else {
      if (sortBy === "date") {
        valA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        valB = new Date(b.transactionDate || b.createdAt || 0).getTime();
      } else if (sortBy === "supplier") {
        valA = String(a.counterpartyName || "").toLowerCase();
        valB = String(b.counterpartyName || "").toLowerCase();
      } else if (sortBy === "invoiceNo") {
        valA = String(a.documentNumber || "").toLowerCase();
        valB = String(b.documentNumber || "").toLowerCase();
      } else if (sortBy === "amount") {
        valA = getInvoiceTotal(a);
        valB = getInvoiceTotal(b);
      } else if (sortBy === "paid") {
        valA = getAmountPaid(a);
        valB = getAmountPaid(b);
      } else if (sortBy === "dueDate" || sortBy === "payDate") {
        valA = new Date(a.payDate || 0).getTime();
        valB = new Date(b.payDate || 0).getTime();
      } else if (sortBy === "pending" || sortBy === "balance") {
        valA = getBalancePending(a);
        valB = getBalancePending(b);
      } else if (sortBy === "status") {
        valA = getPurchaseStatus(a);
        valB = getPurchaseStatus(b);
      } else {
        valA = new Date(a.transactionDate || a.createdAt || 0).getTime();
        valB = new Date(b.transactionDate || b.createdAt || 0).getTime();
      }
    }

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  };

  // Compare helper for Advances
  const compareAdvances = (a, b, sortBy, sortOrder) => {
    let valA, valB;
    if (sortBy === "date") {
      valA = new Date(a.date || a.createdAt || 0).getTime();
      valB = new Date(b.date || b.createdAt || 0).getTime();
    } else if (sortBy === "amount") {
      valA = Number(a.amount || 0);
      valB = Number(b.amount || 0);
    } else if (sortBy === "partyName") {
      valA = String(a.partyName || "").toLowerCase();
      valB = String(b.partyName || "").toLowerCase();
    } else if (sortBy === "workDescription") {
      valA = String(a.workDescription || "").toLowerCase();
      valB = String(b.workDescription || "").toLowerCase();
    } else if (sortBy === "quotationNo") {
      valA = String(a.quotationNo || "").toLowerCase();
      valB = String(b.quotationNo || "").toLowerCase();
    } else if (sortBy === "status") {
      valA = String(a.status || "").toLowerCase();
      valB = String(b.status || "").toLowerCase();
    } else if (sortBy === "expectedBillDate") {
      valA = new Date(a.expectedBillDate || 0).getTime();
      valB = new Date(b.expectedBillDate || 0).getTime();
    } else {
      valA = new Date(a.date || a.createdAt || 0).getTime();
      valB = new Date(b.date || b.createdAt || 0).getTime();
    }

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  };

  // Filtered Sales Invoices
  const filteredSales = useMemo(() => {
    let list = groupedSalesInvoices.filter((entry) => entry.entryType === "sale");

    if (salesFilters.search) {
      const keyword = salesFilters.search.trim().toLowerCase();
      list = list.filter((entry) =>
        [entry.documentNumber, entry.counterpartyName, entry.reference]
          .filter(Boolean)
          .some((val) => String(val).toLowerCase().includes(keyword))
      );
    }

    if (salesFilters.status === "paid") {
      list = list.filter((entry) => getSalesStatus(entry) === "paid");
    } else if (salesFilters.status === "unpaid") {
      list = list.filter((entry) => getSalesStatus(entry) !== "paid");
    } else if (salesFilters.status !== "all") {
      list = list.filter((entry) => getSalesStatus(entry) === salesFilters.status);
    }

    return [...list].sort((a, b) => compareInvoices(a, b, salesFilters.sortBy, salesFilters.sortOrder, "sales"));
  }, [groupedSalesInvoices, salesFilters]);

  // Filtered Purchase Invoices
  const filteredPurchases = useMemo(() => {
    let list = groupedPurchaseInvoices.filter((entry) => entry.entryType === "purchase");

    if (purchaseFilters.search) {
      const keyword = purchaseFilters.search.trim().toLowerCase();
      list = list.filter((entry) =>
        [entry.documentNumber, entry.counterpartyName, entry.reference]
          .filter(Boolean)
          .some((val) => String(val).toLowerCase().includes(keyword))
      );
    }

    if (purchaseFilters.status === "paid") {
      list = list.filter((entry) => getPurchaseStatus(entry) === "cleared");
    } else if (purchaseFilters.status === "unpaid") {
      list = list.filter((entry) => getPurchaseStatus(entry) !== "cleared");
    } else if (purchaseFilters.status !== "all") {
      list = list.filter((entry) => getPurchaseStatus(entry) === purchaseFilters.status);
    }

    return [...list].sort((a, b) => compareInvoices(a, b, purchaseFilters.sortBy, purchaseFilters.sortOrder, "purchase"));
  }, [groupedPurchaseInvoices, purchaseFilters]);

  // Filtered Customer Advances
  const filteredCustomerAdvances = useMemo(() => {
    let list = customerAdvances;

    if (customerAdvanceFilters.search) {
      const q = customerAdvanceFilters.search.trim().toLowerCase();
      list = list.filter(item =>
        [item.partyName, item.workDescription, item.quotationNo, item.referenceNumber, item.notes]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(q))
      );
    }

    if (customerAdvanceFilters.status !== "all") {
      list = list.filter(item => item.status === customerAdvanceFilters.status);
    }

    if (customerAdvanceFilters.dateFrom) {
      list = list.filter(item => new Date(item.date) >= new Date(customerAdvanceFilters.dateFrom));
    }

    if (customerAdvanceFilters.dateTo) {
      const to = new Date(customerAdvanceFilters.dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter(item => new Date(item.date) <= to);
    }

    return [...list].sort((a, b) => compareAdvances(a, b, customerAdvanceFilters.sortBy, customerAdvanceFilters.sortOrder));
  }, [customerAdvances, customerAdvanceFilters]);

  // Filtered Supplier Advances
  const filteredSupplierAdvances = useMemo(() => {
    let list = supplierAdvances;

    if (supplierAdvanceFilters.search) {
      const q = supplierAdvanceFilters.search.trim().toLowerCase();
      list = list.filter(item =>
        [item.partyName, item.workDescription, item.referenceNumber, item.notes]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(q))
      );
    }

    if (supplierAdvanceFilters.status !== "all") {
      list = list.filter(item => item.status === supplierAdvanceFilters.status);
    }

    if (supplierAdvanceFilters.dateFrom) {
      list = list.filter(item => new Date(item.date) >= new Date(supplierAdvanceFilters.dateFrom));
    }

    if (supplierAdvanceFilters.dateTo) {
      const to = new Date(supplierAdvanceFilters.dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter(item => new Date(item.date) <= to);
    }

    return [...list].sort((a, b) => compareAdvances(a, b, supplierAdvanceFilters.sortBy, supplierAdvanceFilters.sortOrder));
  }, [supplierAdvances, supplierAdvanceFilters]);

  // Summaries
  const salesSummary = useMemo(() => ({
    total: filteredSales.reduce((sum, entry) => sum + getInvoiceTotal(entry), 0),
    paid: filteredSales.reduce((sum, entry) => sum + getAmountPaid(entry), 0),
    pending: filteredSales.reduce((sum, entry) => sum + getBalancePending(entry), 0),
    advanceSum: customerAdvances.filter(a => a.status === "active").reduce((sum, a) => sum + Number(a.amount || 0), 0)
  }), [filteredSales, customerAdvances]);

  const purchaseSummary = useMemo(() => ({
    total: filteredPurchases.reduce((sum, entry) => sum + getInvoiceTotal(entry), 0),
    paid: filteredPurchases.reduce((sum, entry) => sum + getAmountPaid(entry), 0),
    balance: filteredPurchases.reduce((sum, entry) => sum + getBalancePending(entry), 0),
    overdueCount: filteredPurchases.filter((entry) => getPurchaseStatus(entry) === "overdue").length,
    advanceSum: supplierAdvances.filter(a => a.status === "active").reduce((sum, a) => sum + Number(a.amount || 0), 0)
  }), [filteredPurchases, supplierAdvances]);

  const customerAdvanceSummary = useMemo(() => ({
    total: filteredCustomerAdvances.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    active: filteredCustomerAdvances.filter(a => a.status === "active").reduce((sum, a) => sum + Number(a.amount || 0), 0),
    invoiced: filteredCustomerAdvances.filter(a => a.status === "invoiced" || a.status === "adjusted").reduce((sum, a) => sum + Number(a.amount || 0), 0),
    count: filteredCustomerAdvances.length
  }), [filteredCustomerAdvances]);

  const supplierAdvanceSummary = useMemo(() => ({
    total: filteredSupplierAdvances.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    active: filteredSupplierAdvances.filter(a => a.status === "active").reduce((sum, a) => sum + Number(a.amount || 0), 0),
    invoiced: filteredSupplierAdvances.filter(a => a.status === "adjusted" || a.status === "invoiced").reduce((sum, a) => sum + Number(a.amount || 0), 0),
    count: filteredSupplierAdvances.length
  }), [filteredSupplierAdvances]);

  // Selection Sets & Active Export slices
  const salesToExport = useMemo(() => {
    if (selectedSalesKeys.size > 0) return filteredSales.filter(item => selectedSalesKeys.has(item._groupKey));
    return filteredSales;
  }, [filteredSales, selectedSalesKeys]);

  const purchaseToExport = useMemo(() => {
    if (selectedPurchaseKeys.size > 0) return filteredPurchases.filter(item => selectedPurchaseKeys.has(item._groupKey));
    return filteredPurchases;
  }, [filteredPurchases, selectedPurchaseKeys]);

  const customerAdvancesToExport = useMemo(() => {
    if (selectedCustomerAdvanceKeys.size > 0) return filteredCustomerAdvances.filter(item => selectedCustomerAdvanceKeys.has(item._id));
    return filteredCustomerAdvances;
  }, [filteredCustomerAdvances, selectedCustomerAdvanceKeys]);

  const supplierAdvancesToExport = useMemo(() => {
    if (selectedSupplierAdvanceKeys.size > 0) return filteredSupplierAdvances.filter(item => selectedSupplierAdvanceKeys.has(item._id));
    return filteredSupplierAdvances;
  }, [filteredSupplierAdvances, selectedSupplierAdvanceKeys]);

  const selectedSalesPendingSum = useMemo(() => {
    return filteredSales
      .filter(item => selectedSalesKeys.has(item._groupKey))
      .reduce((sum, item) => sum + getBalancePending(item), 0);
  }, [filteredSales, selectedSalesKeys]);

  const selectedPurchasePendingSum = useMemo(() => {
    return filteredPurchases
      .filter(item => selectedPurchaseKeys.has(item._groupKey))
      .reduce((sum, item) => sum + getBalancePending(item), 0);
  }, [filteredPurchases, selectedPurchaseKeys]);

  const selectedCustomerAdvanceSum = useMemo(() => {
    return filteredCustomerAdvances
      .filter(item => selectedCustomerAdvanceKeys.has(item._id))
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [filteredCustomerAdvances, selectedCustomerAdvanceKeys]);

  const selectedSupplierAdvanceSum = useMemo(() => {
    return filteredSupplierAdvances
      .filter(item => selectedSupplierAdvanceKeys.has(item._id))
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [filteredSupplierAdvances, selectedSupplierAdvanceKeys]);

  // Toggle selection handlers
  const handleToggleSelectSales = (key) => {
    setSelectedSalesKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleSelectAllSales = () => {
    if (selectedSalesKeys.size > 0 && selectedSalesKeys.size === filteredSales.length) {
      setSelectedSalesKeys(new Set());
    } else {
      setSelectedSalesKeys(new Set(filteredSales.map(e => e._groupKey)));
    }
  };

  const handleToggleSelectPurchase = (key) => {
    setSelectedPurchaseKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleSelectAllPurchase = () => {
    if (selectedPurchaseKeys.size > 0 && selectedPurchaseKeys.size === filteredPurchases.length) {
      setSelectedPurchaseKeys(new Set());
    } else {
      setSelectedPurchaseKeys(new Set(filteredPurchases.map(e => e._groupKey)));
    }
  };

  const handleToggleSelectCustomerAdv = (id) => {
    setSelectedCustomerAdvanceKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAllCustomerAdv = () => {
    if (selectedCustomerAdvanceKeys.size > 0 && selectedCustomerAdvanceKeys.size === filteredCustomerAdvances.length) {
      setSelectedCustomerAdvanceKeys(new Set());
    } else {
      setSelectedCustomerAdvanceKeys(new Set(filteredCustomerAdvances.map(e => e._id)));
    }
  };

  const handleToggleSelectSupplierAdv = (id) => {
    setSelectedSupplierAdvanceKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAllSupplierAdv = () => {
    if (selectedSupplierAdvanceKeys.size > 0 && selectedSupplierAdvanceKeys.size === filteredSupplierAdvances.length) {
      setSelectedSupplierAdvanceKeys(new Set());
    } else {
      setSelectedSupplierAdvanceKeys(new Set(filteredSupplierAdvances.map(e => e._id)));
    }
  };

  // Payment Modal handlers
  const fetchPaymentHistory = useCallback(async (type, entry) => {
    const endpoint = type === "sales" ? "sales" : "purchase";
    const response = await axios.get(
      API_BASE_URL + `/api/invoice-payments/${endpoint}/${encodeURIComponent(entry._groupKey)}`,
      { params: { invoiceNumber: entry.documentNumber || "" } }
    );
    return response.data;
  }, []);

  const openPaymentModal = useCallback(async (type, entry, view = "payment") => {
    const initialBalance = getBalancePending(entry);
    setModalState({
      open: true,
      type,
      view,
      entry,
      history: null,
      loading: true,
      saving: false,
      error: "",
      notice: "",
      fieldErrors: {},
      form: {
        ...buildDefaultModalForm(type, entry),
        amount: initialBalance > 0 ? String(initialBalance) : ""
      }
    });

    try {
      const history = await fetchPaymentHistory(type, entry);
      const updatedBalance = Number(history?.balancePending ?? initialBalance);
      setModalState((current) => ({
        ...current,
        history,
        loading: false,
        form: {
          ...current.form,
          amount: current.form.amount === "" || current.form.amount === String(initialBalance)
            ? (updatedBalance > 0 ? String(updatedBalance) : "")
            : current.form.amount,
          payDate: type === "purchase" ? toDateInput(history?.payDate || entry.payDate) : current.form.payDate
        }
      }));
    } catch (error) {
      setModalState((current) => ({
        ...current,
        loading: false,
        error: error.response?.data?.message || "Failed to load payment details"
      }));
    }
  }, [fetchPaymentHistory]);

  const closePaymentModal = useCallback(() => {
    setModalState((current) => current.saving ? current : {
      open: false,
      type: "sales",
      view: "payment",
      entry: null,
      history: null,
      loading: false,
      saving: false,
      error: "",
      notice: "",
      fieldErrors: {},
      form: buildDefaultModalForm("sales")
    });
  }, []);

  const submitPayment = useCallback(async () => {
    const { type, entry, form, history, saving } = modalState;
    if (!entry || saving) return;

    const nextFieldErrors = {};
    const amount = Number(form.amount || 0);
    const tdsAmount = Number(form.tdsAmount || 0);
    const totalSettleAmount = Number((amount + tdsAmount).toFixed(2));
    const invoiceTotal = Number(history?.totalAmount ?? getInvoiceTotal(entry));
    const paidSoFar = Number(history?.previouslyPaidAmount ?? getAmountPaid(entry));
    let currentBalance = Number(history?.balancePending ?? getBalancePending(entry));
    if (currentBalance === 0 && paidSoFar === 0 && invoiceTotal > 0) {
      currentBalance = invoiceTotal;
    }

    if (type === "purchase" && !form.payDate) nextFieldErrors.payDate = "Pay date is required";
    if (!form.paymentDate) nextFieldErrors.paymentDate = "Payment date is required";
    if (!form.paymentMode) nextFieldErrors.paymentMode = "Payment mode is required";
    if (amount < 0) nextFieldErrors.amount = "Amount cannot be negative";
    if (tdsAmount < 0) nextFieldErrors.tdsAmount = "TDS amount cannot be negative";
    if (totalSettleAmount <= 0) {
      nextFieldErrors.amount = type === "sales" ? "Enter amount receiving or TDS" : "Enter amount paying or TDS";
    } else if (totalSettleAmount > currentBalance + 0.01) {
      nextFieldErrors.amount = "Total settlement cannot exceed the balance";
    }

    if (Object.keys(nextFieldErrors).length) {
      setModalState((current) => ({ ...current, fieldErrors: nextFieldErrors }));
      return;
    }

    try {
      setModalState((current) => ({ ...current, saving: true, error: "", notice: "", fieldErrors: {} }));

      const endpoint = type === "sales" ? "sales" : "purchase";
      await axios.post(API_BASE_URL + `/api/invoice-payments/${endpoint}`, {
        invoiceId: entry._groupKey,
        invoiceNumber: entry.documentNumber || "",
        amount,
        tdsAmount,
        paymentDate: form.paymentDate,
        paymentMode: form.paymentMode,
        referenceNumber: form.referenceNumber,
        notes: form.notes,
        createdBy: getCreatedByName(isAdminView),
        ...(type === "purchase" ? { payDate: form.payDate } : {})
      });

      const updatedHistory = await fetchPaymentHistory(type, entry);
      await loadInvoices();

      setModalState((current) => ({
        ...current,
        history: updatedHistory,
        saving: false,
        notice: type === "sales" ? "Payment received successfully." : "Payment saved successfully.",
        form: {
          ...buildDefaultModalForm(type, entry),
          amount: updatedHistory?.balancePending > 0 ? String(updatedHistory.balancePending) : "",
          tdsAmount: "",
          payDate: type === "purchase" ? toDateInput(updatedHistory?.payDate || entry.payDate) : ""
        }
      }));
    } catch (error) {
      setModalState((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.message || "Failed to save payment"
      }));
    }
  }, [fetchPaymentHistory, isAdminView, loadInvoices, modalState]);

  // ══ ADVANCE MODAL HANDLERS ══

  const openCreateAdvanceModal = (type = "customer") => {
    setAdvanceModalState({
      open: true,
      type,
      mode: "create",
      advanceId: null,
      saving: false,
      error: "",
      fieldErrors: {},
      form: buildDefaultAdvanceForm(type)
    });
  };

  const openEditAdvanceModal = (entry) => {
    setAdvanceModalState({
      open: true,
      type: entry.type || "customer",
      mode: "edit",
      advanceId: entry._id,
      saving: false,
      error: "",
      fieldErrors: {},
      form: buildDefaultAdvanceForm(entry.type, entry)
    });
  };

  const closeAdvanceModal = () => {
    if (advanceModalState.saving) return;
    setAdvanceModalState({
      open: false,
      type: "customer",
      mode: "create",
      advanceId: null,
      saving: false,
      error: "",
      fieldErrors: {},
      form: buildDefaultAdvanceForm("customer")
    });
  };

  const handleAdvanceFieldChange = (field, value) => {
    setAdvanceModalState((current) => ({
      ...current,
      error: "",
      fieldErrors: { ...current.fieldErrors, [field]: "" },
      form: { ...current.form, [field]: value }
    }));
  };

  const handleQuotationSelect = (quotation) => {
    setAdvanceModalState((current) => ({
      ...current,
      form: {
        ...current.form,
        quotationId: quotation._id || "",
        quotationNo: quotation.quotationNo || "",
        quotationAmount: quotation.grandTotal ? String(quotation.grandTotal) : "",
        partyName: current.form.partyName || quotation.customerName || "",
        customerId: current.form.customerId || quotation.customerId || "",
        workDescription: current.form.workDescription || quotation.subject || ""
      }
    }));
  };

  const submitAdvance = async () => {
    const { type, mode, advanceId, form, saving } = advanceModalState;
    if (saving) return;

    const errors = {};
    if (!form.partyName || !form.partyName.trim()) {
      errors.partyName = `${type === "customer" ? "Customer" : "Supplier"} name is required`;
    }
    if (!form.workDescription || !form.workDescription.trim()) {
      errors.workDescription = "Work / description is required";
    }
    const numAmount = Number(form.amount || 0);
    if (isNaN(numAmount) || numAmount <= 0) {
      errors.amount = "Enter a valid advance amount greater than 0";
    }
    if (!form.date) {
      errors.date = "Date is required";
    }

    if (Object.keys(errors).length > 0) {
      setAdvanceModalState((current) => ({ ...current, fieldErrors: errors }));
      return;
    }

    try {
      setAdvanceModalState((current) => ({ ...current, saving: true, error: "" }));

      const payload = {
        type,
        partyName: form.partyName.trim(),
        customerId: form.customerId,
        supplierId: form.supplierId,
        workDescription: form.workDescription.trim(),
        amount: numAmount,
        date: form.date,
        quotationId: form.quotationId,
        quotationNo: form.quotationNo,
        quotationAmount: Number(form.quotationAmount || 0),
        paymentMode: form.paymentMode,
        referenceNumber: form.referenceNumber,
        expectedBillDate: form.expectedBillDate || null,
        notes: form.notes,
        status: form.status,
        invoiceNumber: form.invoiceNumber,
        createdBy: getCreatedByName(isAdminView)
      };

      if (mode === "edit" && advanceId) {
        await axios.put(API_BASE_URL + `/api/advance-payments/${advanceId}`, payload);
      } else {
        await axios.post(API_BASE_URL + "/api/advance-payments", payload);
      }

      await loadAdvances();
      closeAdvanceModal();
    } catch (err) {
      setAdvanceModalState((current) => ({
        ...current,
        saving: false,
        error: err.response?.data?.message || "Failed to save advance payment record"
      }));
    }
  };

  const handleDeleteAdvance = async (entry) => {
    const isCustomer = entry.type === "customer";
    if (!window.confirm(`Are you sure you want to delete this ${isCustomer ? "customer" : "supplier"} advance of ${formatCurrency(entry.amount)}?`)) {
      return;
    }

    try {
      await axios.delete(API_BASE_URL + `/api/advance-payments/${entry._id}`);
      await loadAdvances();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete advance record.");
    }
  };

  const handleUpdateAdvanceStatus = async (entry, newStatus) => {
    let invoiceNumber = "";
    if (newStatus === "invoiced") {
      const invInput = prompt("Enter the Sales/Purchase Invoice # this advance is linked to (optional):");
      if (invInput === null) return;
      invoiceNumber = invInput.trim();
    }

    try {
      await axios.put(API_BASE_URL + `/api/advance-payments/${entry._id}/status`, {
        status: newStatus,
        invoiceNumber
      });
      await loadAdvances();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update status.");
    }
  };

  // Search button handlers
  const handleSalesSearch = () => {
    setSalesFilters(prev => ({ ...prev, search: salesSearchVal }));
    setSalesLimit(50);
  };

  const handlePurchaseSearch = () => {
    setPurchaseFilters(prev => ({ ...prev, search: purchaseSearchVal }));
    setPurchaseLimit(50);
  };

  const handleCustomerAdvSearch = () => {
    setCustomerAdvanceFilters(prev => ({ ...prev, search: customerAdvSearchVal }));
  };

  const handleSupplierAdvSearch = () => {
    setSupplierAdvanceFilters(prev => ({ ...prev, search: supplierAdvSearchVal }));
  };

  const visibleSales = useMemo(() => filteredSales.slice(0, salesLimit), [filteredSales, salesLimit]);
  const visiblePurchases = useMemo(() => filteredPurchases.slice(0, purchaseLimit), [filteredPurchases, purchaseLimit]);

  const hasMoreSales = filteredSales.length > salesLimit;
  const hasMorePurchases = filteredPurchases.length > purchaseLimit;

  return (
    <div className="mobile-dense-page space-y-4 lg:space-y-6">
      {/* Top Banner */}
      <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-900 via-slate-800 to-stone-900 p-5 sm:p-6 text-white shadow-lg">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400 backdrop-blur-md border border-white/10">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              {isAdminView ? "Admin Accounts Monitor" : "Accounts Monitor"}
            </div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight text-white">Accounts Monitor</h1>
            <p className="mt-1 max-w-2xl text-xs sm:text-sm text-slate-300 font-normal leading-relaxed">
              Track invoices, live balances, customer advances (pre-invoice), supplier advances, and export Excel / PDF reports with pending sort and selection.
            </p>
          </div>
        </div>
      </section>

      {/* Main Top Tab Switcher */}
      <div className="flex rounded-2xl bg-slate-200/80 p-1.5 max-w-lg mx-auto text-xs font-bold shadow-inner border border-slate-300/60 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => setActiveTab("sales")}
          className={`flex-1 rounded-xl py-2.5 transition-all text-center flex items-center justify-center gap-2 ${
            activeTab === "sales"
              ? "bg-slate-950 text-white shadow-md shadow-slate-900/20"
              : "text-slate-600 hover:text-slate-950 hover:bg-white/50"
          }`}
        >
          <span>📈</span>
          <span>Sales Monitor</span>
          <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
            activeTab === "sales" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-slate-300/80 text-slate-700"
          }`}>
            {filteredSales.length + filteredCustomerAdvances.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("purchase")}
          className={`flex-1 rounded-xl py-2.5 transition-all text-center flex items-center justify-center gap-2 ${
            activeTab === "purchase"
              ? "bg-slate-950 text-white shadow-md shadow-slate-900/20"
              : "text-slate-600 hover:text-slate-950 hover:bg-white/50"
          }`}
        >
          <span>📉</span>
          <span>Purchase Monitor</span>
          <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
            activeTab === "purchase" ? "bg-rose-500/20 text-rose-300 border border-rose-500/30" : "bg-slate-300/80 text-slate-700"
          }`}>
            {filteredPurchases.length + filteredSupplierAdvances.length}
          </span>
        </button>
      </div>

      {activeTab === "sales" ? (
        /* ══════════════════════════════════════════════
           ══ SALES MONITOR SECTION (INVOICES & ADVANCES) ══
           ══════════════════════════════════════════════ */
        <section className="space-y-4">
          {/* Sub-view switcher: Invoices vs Customer Advances */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-3">
            <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200 text-xs font-bold shadow-xs">
              <button
                type="button"
                onClick={() => setSalesSubView("invoices")}
                className={`rounded-lg px-4 py-2 transition-all flex items-center gap-2 ${
                  salesSubView === "invoices"
                    ? "bg-white text-slate-950 shadow-sm border border-slate-200/60 font-black"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                <span>📜</span>
                <span>Sales Invoices</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                  salesSubView === "invoices" ? "bg-emerald-100 text-emerald-800 font-bold" : "bg-slate-200 text-slate-600"
                }`}>
                  {filteredSales.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSalesSubView("advances")}
                className={`rounded-lg px-4 py-2 transition-all flex items-center gap-2 ${
                  salesSubView === "advances"
                    ? "bg-emerald-700 text-white shadow-sm font-black"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                <span>💰</span>
                <span>Customer Advances (Pre-Invoice)</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                  salesSubView === "advances" ? "bg-white text-emerald-900 font-bold" : "bg-slate-200 text-slate-600"
                }`}>
                  {filteredCustomerAdvances.length}
                </span>
              </button>
            </div>
          </div>

          {salesSubView === "invoices" ? (
            /* ── Sales Invoices Sub-View ── */
            <div className="space-y-4">
              <SalesSummaryCards pending={salesSummary.pending} collected={salesSummary.paid} advanceSum={salesSummary.advanceSum} />

              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                {/* Header Toolbar */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="text-emerald-600 text-sm">📈</span> Sales Invoices
                    </h2>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Showing {visibleSales.length} of {filteredSales.length} invoices
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Unpaid / Paid / All switch */}
                    <div className="flex rounded-lg bg-slate-100 p-1 text-xs font-semibold shrink-0">
                      <button
                        type="button"
                        onClick={() => setSalesFilters(prev => ({ ...prev, status: "unpaid" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          salesFilters.status === "unpaid"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Unpaid
                      </button>
                      <button
                        type="button"
                        onClick={() => setSalesFilters(prev => ({ ...prev, status: "paid" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          salesFilters.status === "paid"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Paid
                      </button>
                      <button
                        type="button"
                        onClick={() => setSalesFilters(prev => ({ ...prev, status: "all" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          salesFilters.status === "all"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        All
                      </button>
                    </div>

                    {/* Sort Dropdown */}
                    <select
                      value={`${salesFilters.sortBy}_${salesFilters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split("_");
                        setSalesFilters(prev => ({ ...prev, sortBy, sortOrder }));
                      }}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 h-9 outline-none focus:border-blue-500"
                    >
                      <option value="pending_desc">Sort: Pending (High → Low)</option>
                      <option value="pending_asc">Sort: Pending (Low → High)</option>
                      <option value="date_desc">Sort: Date (Newest)</option>
                      <option value="date_asc">Sort: Date (Oldest)</option>
                      <option value="amount_desc">Sort: Amount (High → Low)</option>
                      <option value="customer_asc">Sort: Customer (A → Z)</option>
                    </select>

                    {/* Search Input */}
                    <div className="flex items-center gap-1">
                      <input
                        value={salesSearchVal}
                        onChange={(e) => setSalesSearchVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSalesSearch()}
                        placeholder="Search invoice, customer..."
                        className="rounded-[4px] border border-slate-300 px-3 py-1 text-xs outline-none focus:border-blue-500 h-9 bg-white w-36 sm:w-48 text-slate-700"
                      />
                      <button
                        type="button"
                        onClick={handleSalesSearch}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSalesFilters(prev => !prev)}
                        className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2.5 h-9 transition"
                        title="Toggle Date Filter"
                      >
                        📅
                      </button>
                    </div>

                    {/* Export Buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => exportAccountsToExcel("sales", salesToExport)}
                        className="rounded-[4px] bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📊</span>
                        <span>Excel {selectedSalesKeys.size > 0 ? `(${selectedSalesKeys.size})` : `(${filteredSales.length})`}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAccountsToPdf("sales", salesToExport)}
                        className="rounded-[4px] bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📄</span>
                        <span>PDF {selectedSalesKeys.size > 0 ? `(${selectedSalesKeys.size})` : `(${filteredSales.length})`}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selection Bar */}
                {selectedSalesKeys.size > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-950 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">✓ {selectedSalesKeys.size} of {filteredSales.length} Invoices Selected</span>
                      <span className="text-emerald-700">| Selected Pending: <strong className="text-emerald-950 font-bold">{formatCurrency(selectedSalesPendingSum)}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleSelectAllSales}
                        className="rounded bg-white border border-emerald-300 px-2 py-1 font-semibold text-emerald-900 hover:bg-emerald-100 transition"
                      >
                        {selectedSalesKeys.size === filteredSales.length ? "Deselect All" : `Select All (${filteredSales.length})`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSalesKeys(new Set())}
                        className="rounded bg-white border border-emerald-300 px-2 py-1 font-semibold text-emerald-900 hover:bg-emerald-100 transition"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Collapsible Date Filter */}
                {showSalesFilters && (
                  <SalesFilters
                    filters={salesFilters}
                    onChange={(field, val) => setSalesFilters(prev => ({ ...prev, [field]: val }))}
                    onReset={() => setSalesFilters(prev => ({ ...prev, dateFrom: "", dateTo: "" }))}
                  />
                )}

                {/* Sales Table */}
                <div className="mt-4">
                  <SalesTable
                    data={visibleSales}
                    loading={loading}
                    onAction={(view, entry) => openPaymentModal("sales", entry, view)}
                    isAdmin={isAdmin}
                    onEditInvoice={handleEditSalesInvoice}
                    selectedKeys={selectedSalesKeys}
                    onToggleSelect={handleToggleSelectSales}
                    onToggleSelectAll={handleToggleSelectAllSales}
                    sortBy={salesFilters.sortBy}
                    sortOrder={salesFilters.sortOrder}
                    onSort={(sortKey) => {
                      setSalesFilters(prev => ({
                        ...prev,
                        sortBy: sortKey,
                        sortOrder: prev.sortBy === sortKey && prev.sortOrder === "desc" ? "asc" : "desc"
                      }));
                    }}
                  />
                </div>

                <ViewMoreButton onClick={() => setSalesLimit(prev => prev + 50)} hasMore={hasMoreSales} />
              </div>
            </div>
          ) : (
            /* ── Customer Advances Sub-View (Pre-Invoice) ── */
            <div className="space-y-4">
              <CustomerAdvanceSummaryCards
                totalAdvance={customerAdvanceSummary.total}
                activeAdvance={customerAdvanceSummary.active}
                invoicedAdvance={customerAdvanceSummary.invoiced}
                count={customerAdvanceSummary.count}
              />

              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                {/* Header Toolbar */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="text-emerald-600 text-sm">💰</span> Customer Advances (Pre-Invoice Sales Advances)
                    </h2>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Track customer advances before billing with optional quotation reference
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Status filter */}
                    <select
                      value={customerAdvanceFilters.status}
                      onChange={(e) => setCustomerAdvanceFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 h-9 outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active (Advance Held)</option>
                      <option value="invoiced">Invoiced</option>
                      <option value="adjusted">Adjusted</option>
                      <option value="refunded">Refunded</option>
                    </select>

                    {/* Sort Dropdown */}
                    <select
                      value={`${customerAdvanceFilters.sortBy}_${customerAdvanceFilters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split("_");
                        setCustomerAdvanceFilters(prev => ({ ...prev, sortBy, sortOrder }));
                      }}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 h-9 outline-none focus:border-emerald-500"
                    >
                      <option value="date_desc">Sort: Date (Newest)</option>
                      <option value="date_asc">Sort: Date (Oldest)</option>
                      <option value="amount_desc">Sort: Amount (High → Low)</option>
                      <option value="amount_asc">Sort: Amount (Low → High)</option>
                      <option value="partyName_asc">Sort: Customer (A → Z)</option>
                      <option value="quotationNo_asc">Sort: Quotation #</option>
                    </select>

                    {/* Search Input */}
                    <div className="flex items-center gap-1">
                      <input
                        value={customerAdvSearchVal}
                        onChange={(e) => setCustomerAdvSearchVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCustomerAdvSearch()}
                        placeholder="Search customer, work, quotation..."
                        className="rounded-[4px] border border-slate-300 px-3 py-1 text-xs outline-none focus:border-emerald-500 h-9 bg-white w-40 sm:w-52 text-slate-700"
                      />
                      <button
                        type="button"
                        onClick={handleCustomerAdvSearch}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCustomerAdvFilters(prev => !prev)}
                        className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2.5 h-9 transition"
                        title="Toggle Date Filter"
                      >
                        📅
                      </button>
                    </div>

                    {/* Action & Export Buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => openCreateAdvanceModal("customer")}
                        className="rounded-[4px] bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-3.5 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>+</span>
                        <span>Record Customer Advance</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAdvancesToExcel("customer", customerAdvancesToExport)}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📊</span>
                        <span>Excel {selectedCustomerAdvanceKeys.size > 0 ? `(${selectedCustomerAdvanceKeys.size})` : `(${filteredCustomerAdvances.length})`}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAdvancesToPdf("customer", customerAdvancesToExport)}
                        className="rounded-[4px] bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📄</span>
                        <span>PDF {selectedCustomerAdvanceKeys.size > 0 ? `(${selectedCustomerAdvanceKeys.size})` : `(${filteredCustomerAdvances.length})`}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selection Banner */}
                {selectedCustomerAdvanceKeys.size > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-950 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">✓ {selectedCustomerAdvanceKeys.size} of {filteredCustomerAdvances.length} Advances Selected</span>
                      <span className="text-emerald-700">| Total Advance: <strong className="text-emerald-950 font-bold">{formatCurrency(selectedCustomerAdvanceSum)}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleSelectAllCustomerAdv}
                        className="rounded bg-white border border-emerald-300 px-2 py-1 font-semibold text-emerald-900 hover:bg-emerald-100 transition"
                      >
                        {selectedCustomerAdvanceKeys.size === filteredCustomerAdvances.length ? "Deselect All" : `Select All (${filteredCustomerAdvances.length})`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedCustomerAdvanceKeys(new Set())}
                        className="rounded bg-white border border-emerald-300 px-2 py-1 font-semibold text-emerald-900 hover:bg-emerald-100 transition"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Collapsible Date Filter */}
                {showCustomerAdvFilters && (
                  <div className="flex flex-wrap items-end gap-3 text-xs bg-slate-50 p-3 rounded-[4px] border border-slate-200">
                    <div>
                      <label className="mb-1 block font-semibold text-stone-500">From Date</label>
                      <input
                        type="date"
                        value={customerAdvanceFilters.dateFrom || ""}
                        onChange={(e) => setCustomerAdvanceFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                        className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block font-semibold text-stone-500">To Date</label>
                      <input
                        type="date"
                        value={customerAdvanceFilters.dateTo || ""}
                        onChange={(e) => setCustomerAdvanceFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                        className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setCustomerAdvanceFilters(prev => ({ ...prev, dateFrom: "", dateTo: "" }))}
                      className="rounded-[4px] border border-stone-300 px-3 h-8 font-semibold text-stone-700 transition hover:bg-stone-50 bg-white"
                    >
                      Reset Date
                    </button>
                  </div>
                )}

                {/* Customer Advances Table */}
                <div className="mt-4">
                  <CustomerAdvancesTable
                    data={filteredCustomerAdvances}
                    loading={advancesLoading}
                    onEdit={openEditAdvanceModal}
                    onDelete={handleDeleteAdvance}
                    onStatusChange={handleUpdateAdvanceStatus}
                    isAdmin={isAdmin}
                    selectedKeys={selectedCustomerAdvanceKeys}
                    onToggleSelect={handleToggleSelectCustomerAdv}
                    onToggleSelectAll={handleToggleSelectAllCustomerAdv}
                    sortBy={customerAdvanceFilters.sortBy}
                    sortOrder={customerAdvanceFilters.sortOrder}
                    onSort={(sortKey) => {
                      setCustomerAdvanceFilters(prev => ({
                        ...prev,
                        sortBy: sortKey,
                        sortOrder: prev.sortBy === sortKey && prev.sortOrder === "desc" ? "asc" : "desc"
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      ) : (
        /* ═════════════════════════════════════════════════
           ══ PURCHASE MONITOR SECTION (INVOICES & ADVANCES) ══
           ═════════════════════════════════════════════════ */
        <section className="space-y-4">
          {/* Sub-view switcher: Invoices vs Supplier Advances */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-3">
            <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200 text-xs font-bold shadow-xs">
              <button
                type="button"
                onClick={() => setPurchaseSubView("invoices")}
                className={`rounded-lg px-4 py-2 transition-all flex items-center gap-2 ${
                  purchaseSubView === "invoices"
                    ? "bg-white text-slate-950 shadow-sm border border-slate-200/60 font-black"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                <span>📜</span>
                <span>Purchase Invoices</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                  purchaseSubView === "invoices" ? "bg-rose-100 text-rose-800 font-bold" : "bg-slate-200 text-slate-600"
                }`}>
                  {filteredPurchases.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setPurchaseSubView("advances")}
                className={`rounded-lg px-4 py-2 transition-all flex items-center gap-2 ${
                  purchaseSubView === "advances"
                    ? "bg-rose-700 text-white shadow-sm font-black"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                <span>💸</span>
                <span>Supplier Advances</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                  purchaseSubView === "advances" ? "bg-white text-rose-900 font-bold" : "bg-slate-200 text-slate-600"
                }`}>
                  {filteredSupplierAdvances.length}
                </span>
              </button>
            </div>
          </div>

          {purchaseSubView === "invoices" ? (
            /* ── Purchase Invoices Sub-View ── */
            <div className="space-y-4">
              <PurchaseSummaryCards
                balance={purchaseSummary.balance}
                overdueCount={purchaseSummary.overdueCount}
                advanceSum={purchaseSummary.advanceSum}
              />

              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                {/* Header Toolbar */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="text-rose-600 text-sm">📉</span> Purchase Invoices
                    </h2>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Showing {visiblePurchases.length} of {filteredPurchases.length} invoices
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Unpaid / Paid / All switch */}
                    <div className="flex rounded-lg bg-slate-100 p-1 text-xs font-semibold shrink-0">
                      <button
                        type="button"
                        onClick={() => setPurchaseFilters(prev => ({ ...prev, status: "unpaid" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          purchaseFilters.status === "unpaid"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Unpaid
                      </button>
                      <button
                        type="button"
                        onClick={() => setPurchaseFilters(prev => ({ ...prev, status: "paid" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          purchaseFilters.status === "paid"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        Paid
                      </button>
                      <button
                        type="button"
                        onClick={() => setPurchaseFilters(prev => ({ ...prev, status: "all" }))}
                        className={`rounded-md px-3 py-1.5 transition ${
                          purchaseFilters.status === "all"
                            ? "bg-white text-slate-800 shadow-sm border border-slate-200/50"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        All
                      </button>
                    </div>

                    {/* Sort Dropdown */}
                    <select
                      value={`${purchaseFilters.sortBy}_${purchaseFilters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split("_");
                        setPurchaseFilters(prev => ({ ...prev, sortBy, sortOrder }));
                      }}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 h-9 outline-none focus:border-blue-500"
                    >
                      <option value="pending_desc">Sort: Balance to Pay (High → Low)</option>
                      <option value="pending_asc">Sort: Balance to Pay (Low → High)</option>
                      <option value="dueDate_asc">Sort: Due Date (Upcoming)</option>
                      <option value="date_desc">Sort: Date (Newest)</option>
                      <option value="amount_desc">Sort: Amount (High → Low)</option>
                      <option value="supplier_asc">Sort: Supplier (A → Z)</option>
                    </select>

                    {/* Search Input */}
                    <div className="flex items-center gap-1">
                      <input
                        value={purchaseSearchVal}
                        onChange={(e) => setPurchaseSearchVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handlePurchaseSearch()}
                        placeholder="Search invoice, supplier..."
                        className="rounded-[4px] border border-slate-300 px-3 py-1 text-xs outline-none focus:border-blue-500 h-9 bg-white w-36 sm:w-48 text-slate-700"
                      />
                      <button
                        type="button"
                        onClick={handlePurchaseSearch}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPurchaseFilters(prev => !prev)}
                        className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2.5 h-9 transition"
                        title="Toggle Date Filter"
                      >
                        📅
                      </button>
                    </div>

                    {/* Export Buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => exportAccountsToExcel("purchase", purchaseToExport)}
                        className="rounded-[4px] bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📊</span>
                        <span>Excel {selectedPurchaseKeys.size > 0 ? `(${selectedPurchaseKeys.size})` : `(${filteredPurchases.length})`}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAccountsToPdf("purchase", purchaseToExport)}
                        className="rounded-[4px] bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📄</span>
                        <span>PDF {selectedPurchaseKeys.size > 0 ? `(${selectedPurchaseKeys.size})` : `(${filteredPurchases.length})`}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selection Bar */}
                {selectedPurchaseKeys.size > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-950 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">✓ {selectedPurchaseKeys.size} of {filteredPurchases.length} Invoices Selected</span>
                      <span className="text-rose-700">| Selected Balance: <strong className="text-rose-950 font-bold">{formatCurrency(selectedPurchasePendingSum)}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleSelectAllPurchase}
                        className="rounded bg-white border border-rose-300 px-2 py-1 font-semibold text-rose-900 hover:bg-rose-100 transition"
                      >
                        {selectedPurchaseKeys.size === filteredPurchases.length ? "Deselect All" : `Select All (${filteredPurchases.length})`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPurchaseKeys(new Set())}
                        className="rounded bg-white border border-rose-300 px-2 py-1 font-semibold text-rose-900 hover:bg-rose-100 transition"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Collapsible Date Filter */}
                {showPurchaseFilters && (
                  <PurchaseFilters
                    filters={purchaseFilters}
                    onChange={(field, val) => setPurchaseFilters(prev => ({ ...prev, [field]: val }))}
                    onReset={() => setPurchaseFilters(prev => ({ ...prev, dateFrom: "", dateTo: "" }))}
                  />
                )}

                {/* Purchase Table */}
                <div className="mt-4">
                  <PurchaseTable
                    data={visiblePurchases}
                    loading={loading}
                    onAction={(view, entry) => openPaymentModal("purchase", entry, view)}
                    isAdmin={isAdmin}
                    onEditInvoice={handleEditPurchaseInvoice}
                    selectedKeys={selectedPurchaseKeys}
                    onToggleSelect={handleToggleSelectPurchase}
                    onToggleSelectAll={handleToggleSelectAllPurchase}
                    sortBy={purchaseFilters.sortBy}
                    sortOrder={purchaseFilters.sortOrder}
                    onSort={(sortKey) => {
                      setPurchaseFilters(prev => ({
                        ...prev,
                        sortBy: sortKey,
                        sortOrder: prev.sortBy === sortKey && prev.sortOrder === "desc" ? "asc" : "desc"
                      }));
                    }}
                  />
                </div>

                <ViewMoreButton onClick={() => setPurchaseLimit(prev => prev + 50)} hasMore={hasMorePurchases} />
              </div>
            </div>
          ) : (
            /* ── Supplier Advances Sub-View ── */
            <div className="space-y-4">
              <SupplierAdvanceSummaryCards
                totalAdvance={supplierAdvanceSummary.total}
                activeAdvance={supplierAdvanceSummary.active}
                invoicedAdvance={supplierAdvanceSummary.invoiced}
                count={supplierAdvanceSummary.count}
              />

              <div className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                {/* Header Toolbar */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="text-rose-600 text-sm">💸</span> Supplier Advances
                    </h2>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Track advance payments made to suppliers and vendors before bills are received
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Status filter */}
                    <select
                      value={supplierAdvanceFilters.status}
                      onChange={(e) => setSupplierAdvanceFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 h-9 outline-none focus:border-rose-500"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active (Pending Bill)</option>
                      <option value="adjusted">Adjusted / Settled</option>
                      <option value="refunded">Refunded</option>
                    </select>

                    {/* Sort Dropdown */}
                    <select
                      value={`${supplierAdvanceFilters.sortBy}_${supplierAdvanceFilters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split("_");
                        setSupplierAdvanceFilters(prev => ({ ...prev, sortBy, sortOrder }));
                      }}
                      className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 h-9 outline-none focus:border-rose-500"
                    >
                      <option value="date_desc">Sort: Date (Newest)</option>
                      <option value="date_asc">Sort: Date (Oldest)</option>
                      <option value="amount_desc">Sort: Amount (High → Low)</option>
                      <option value="amount_asc">Sort: Amount (Low → High)</option>
                      <option value="partyName_asc">Sort: Supplier (A → Z)</option>
                    </select>

                    {/* Search Input */}
                    <div className="flex items-center gap-1">
                      <input
                        value={supplierAdvSearchVal}
                        onChange={(e) => setSupplierAdvSearchVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSupplierAdvSearch()}
                        placeholder="Search supplier, purpose..."
                        className="rounded-[4px] border border-slate-300 px-3 py-1 text-xs outline-none focus:border-rose-500 h-9 bg-white w-40 sm:w-52 text-slate-700"
                      />
                      <button
                        type="button"
                        onClick={handleSupplierAdvSearch}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSupplierAdvFilters(prev => !prev)}
                        className="rounded-[4px] border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2.5 h-9 transition"
                        title="Toggle Date Filter"
                      >
                        📅
                      </button>
                    </div>

                    {/* Action & Export Buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => openCreateAdvanceModal("supplier")}
                        className="rounded-[4px] bg-rose-700 hover:bg-rose-800 text-white text-xs font-bold px-3.5 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>+</span>
                        <span>Record Supplier Advance</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAdvancesToExcel("supplier", supplierAdvancesToExport)}
                        className="rounded-[4px] bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📊</span>
                        <span>Excel {selectedSupplierAdvanceKeys.size > 0 ? `(${selectedSupplierAdvanceKeys.size})` : `(${filteredSupplierAdvances.length})`}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => exportAdvancesToPdf("supplier", supplierAdvancesToExport)}
                        className="rounded-[4px] bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold px-3 h-9 transition flex items-center gap-1.5 shadow-sm"
                      >
                        <span>📄</span>
                        <span>PDF {selectedSupplierAdvanceKeys.size > 0 ? `(${selectedSupplierAdvanceKeys.size})` : `(${filteredSupplierAdvances.length})`}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selection Banner */}
                {selectedSupplierAdvanceKeys.size > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-950 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">✓ {selectedSupplierAdvanceKeys.size} of {filteredSupplierAdvances.length} Advances Selected</span>
                      <span className="text-rose-700">| Total Advance: <strong className="text-rose-950 font-bold">{formatCurrency(selectedSupplierAdvanceSum)}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleToggleSelectAllSupplierAdv}
                        className="rounded bg-white border border-rose-300 px-2 py-1 font-semibold text-rose-900 hover:bg-rose-100 transition"
                      >
                        {selectedSupplierAdvanceKeys.size === filteredSupplierAdvances.length ? "Deselect All" : `Select All (${filteredSupplierAdvances.length})`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSupplierAdvanceKeys(new Set())}
                        className="rounded bg-white border border-rose-300 px-2 py-1 font-semibold text-rose-900 hover:bg-rose-100 transition"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Collapsible Date Filter */}
                {showSupplierAdvFilters && (
                  <div className="flex flex-wrap items-end gap-3 text-xs bg-slate-50 p-3 rounded-[4px] border border-slate-200">
                    <div>
                      <label className="mb-1 block font-semibold text-stone-500">From Date</label>
                      <input
                        type="date"
                        value={supplierAdvanceFilters.dateFrom || ""}
                        onChange={(e) => setSupplierAdvanceFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                        className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block font-semibold text-stone-500">To Date</label>
                      <input
                        type="date"
                        value={supplierAdvanceFilters.dateTo || ""}
                        onChange={(e) => setSupplierAdvanceFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                        className="rounded-[4px] border border-stone-300 px-2 py-1 h-8 outline-none focus:border-stone-500 bg-white text-stone-700 w-36"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSupplierAdvanceFilters(prev => ({ ...prev, dateFrom: "", dateTo: "" }))}
                      className="rounded-[4px] border border-stone-300 px-3 h-8 font-semibold text-stone-700 transition hover:bg-stone-50 bg-white"
                    >
                      Reset Date
                    </button>
                  </div>
                )}

                {/* Supplier Advances Table */}
                <div className="mt-4">
                  <SupplierAdvancesTable
                    data={filteredSupplierAdvances}
                    loading={advancesLoading}
                    onEdit={openEditAdvanceModal}
                    onDelete={handleDeleteAdvance}
                    onStatusChange={handleUpdateAdvanceStatus}
                    isAdmin={isAdmin}
                    selectedKeys={selectedSupplierAdvanceKeys}
                    onToggleSelect={handleToggleSelectSupplierAdv}
                    onToggleSelectAll={handleToggleSelectAllSupplierAdv}
                    sortBy={supplierAdvanceFilters.sortBy}
                    sortOrder={supplierAdvanceFilters.sortOrder}
                    onSort={(sortKey) => {
                      setSupplierAdvanceFilters(prev => ({
                        ...prev,
                        sortBy: sortKey,
                        sortOrder: prev.sortBy === sortKey && prev.sortOrder === "desc" ? "asc" : "desc"
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Payment Settlement Modal */}
      <PaymentModal
        modalState={modalState}
        onClose={closePaymentModal}
        onViewChange={(view) => setModalState(prev => ({ ...prev, view }))}
        onFieldChange={(field, value) => {
          setModalState(prev => ({
            ...prev,
            fieldErrors: { ...prev.fieldErrors, [field]: "" },
            form: { ...prev.form, [field]: value }
          }));
        }}
        onSubmit={submitPayment}
        isAdmin={isAdmin}
        onEditInvoice={(entry) => handleEditInvoiceFromModal(modalState.type, entry || modalState.entry)}
      />

      {/* Advance Record & Edit Modal */}
      <AdvanceModal
        modalState={advanceModalState}
        onClose={closeAdvanceModal}
        onFieldChange={handleAdvanceFieldChange}
        onQuotationSelect={handleQuotationSelect}
        onSubmit={submitAdvance}
        customersList={customersList}
        dealersList={dealersList}
        quotationsList={quotationsList}
      />
    </div>
  );
}
